/**
 * Lofi generator engine.
 * Ported from lofigenerator.com (v0.5.7 Beta) with the gracious permission of
 * its creator Dylan Turner (https://dylanturner.dev).
 *
 * Original work © Dylan Turner. Used here with direct permission for
 * integration into Thingdoms (a non-commercial art project).
 */

import * as Tone from 'tone';

// Self-hosted samples. Downloaded from lofigenerator.com with Dylan's
// permission. Local path avoids CORS issues (his CDN doesn't send CORS headers).
const SAMPLE_BASE = '/samples/lofigen/';

// -------------------------------------------------------------------------
// Utility functions
// -------------------------------------------------------------------------

// Random float in [min, max)
function between(min, max) {
    const range = max - min;
    return min + Math.random() * range;
}

// Tone.js v15 sometimes exposes what used to be plain props (feedback, depth)
// as Signals (.value) and sometimes as plain numbers. This helper tries both.
function setScalarOrSignal(node, prop, value) {
    if (!node || !(prop in node)) return;
    try {
        const current = node[prop];
        if (current && typeof current === 'object' && 'value' in current) {
            current.value = value;
        } else {
            node[prop] = value;
        }
    } catch (e) {
        // Silently fail — not worth blocking playback over a single FX param
    }
}

// Boolean probability — prob in [0,1]
function chance(prob) {
    if (prob <= 0) return false;
    return Math.random() < prob;
}

// Choose one item from arr. Optional probs normalises weights.
function choose(arr, probs) {
    let total = 0;
    let invalid = true;
    if (probs && probs.length === arr.length) {
        invalid = false;
        probs.forEach((prob) => {
            if (typeof prob === 'number' && prob >= 0) {
                total += prob;
            } else {
                invalid = true;
            }
        });
    }
    if (invalid || total === 0) {
        probs = [];
        for (let i = 0; i < arr.length; i++) probs.push(1 / arr.length);
    } else {
        probs = probs.map((x) => (x * 1) / total);
    }

    const rand = Math.random();
    let cumulativeProb = 0;
    for (let i = 0; i < arr.length; i++) {
        cumulativeProb += probs[i];
        if (cumulativeProb > rand) return arr[i];
    }
    return arr[arr.length - 1];
}

// Takes [thing, probability] pairs and returns an array of 'things' that hit.
function chooseSome(...items) {
    const arr = [];
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (chance(item[1])) arr.push(item[0]);
    }
    return arr;
}

// -------------------------------------------------------------------------
// Music theory: root notes + chord type voicings
// -------------------------------------------------------------------------

const rt = {
    C: 0, Cs: 1, D: 2, Ds: 3, E: 4, F: 5,
    Fs: 6, G: 7, Gs: 8, A: 9, As: 10, B: 11,
};

const tp = {
    Triad: [0, 4, 7],
    FirstInv: [4, 7, 0],
    SecondInv: [7, 0, 4],
    Maj6th: [0, 4, 7, 9],
    Dom7th: [0, 4, 7, 10],
    Dom7thThirdInv: [10, 0, 4, 7],
    Maj7th: [0, 4, 7, 11],
    Maj7thThirdInv: [11, 0, 4, 7],
    Add9: [0, 4, 7, 2],
    Add9ThirdInv: [2, 4, 7, 0],

    Sus2: [0, 2, 7],
    Sus4: [0, 5, 7],
    Sus42: [0, 2, 5, 7],

    MinTriad: [0, 3, 7],
    MinFirstInv: [3, 7, 0],
    MinSecondInv: [7, 0, 3],
    Min6th: [0, 3, 7, 9],
    Min7th: [0, 3, 7, 10],
    Min7thThirdInv: [10, 0, 3, 7],
    MinAdd9: [0, 3, 7, 2],
    MinAdd9ThirdInv: [2, 3, 7, 0],

    Maj7th9th: [0, 4, 7, 11, 2],
    Min7th9th: [0, 3, 7, 10, 2],
    Dom7th9th: [0, 4, 7, 10, 2],

    Dom7thFlat9: [0, 4, 7, 10, 1],
    Dim: [0, 3, 6, 9],
    HalfDim: [0, 3, 6, 10],

    // Thingdoms additions — more colour for new progression templates
    Maj7Sharp11: [0, 4, 7, 11, 6],   // dreamy lydian-tinted tonic (I△7#11)
    Maj9:        [0, 4, 7, 11, 2],   // alias of Maj7th9th, kept for clarity in new templates
    MinMaj7:     [0, 3, 7, 11],      // tension chord (bond theme flavour)
    Sus4Add9:    [0, 2, 5, 7],       // open/floating — same intervals as Sus42
};

// -------------------------------------------------------------------------
// Chord progression library
// -------------------------------------------------------------------------

// Scale definitions — all 7 diatonic modes.
const MAJOR_SCALE      = [0, 2, 4, 5, 7, 9, 11];  // ionian (classic lofi)
const MINOR_SCALE      = [0, 2, 3, 5, 7, 8, 10];  // natural minor / aeolian (moody)
const DORIAN_SCALE     = [0, 2, 3, 5, 7, 9, 10];  // minor with raised 6th (bittersweet)
const PHRYGIAN_SCALE   = [0, 1, 3, 5, 7, 8, 10];  // minor with flat 2nd (dark, exotic)
const LYDIAN_SCALE     = [0, 2, 4, 6, 7, 9, 11];  // major with raised 4th (dreamy)
const MIXOLYDIAN_SCALE = [0, 2, 4, 5, 7, 9, 10];  // major with flat 7th (bluesy, old-timey)
const LOCRIAN_SCALE    = [0, 1, 3, 5, 6, 8, 10];  // flat 2 + flat 5 (unresolved, uneasy)

const MODE_SCALES = {
    major:      MAJOR_SCALE,
    minor:      MINOR_SCALE,
    dorian:     DORIAN_SCALE,
    phrygian:   PHRYGIAN_SCALE,
    lydian:     LYDIAN_SCALE,
    mixolydian: MIXOLYDIAN_SCALE,
    locrian:    LOCRIAN_SCALE,
};

// Thingdoms extension: minor-key progressions. Picked when song.isMinor is true.
// These mirror the structure of Dylan's getProg but with minor-key chord
// functions (i, iiø, III, iv, V7, VI, VII).
function getMinorProg(/* key */) {
    const progType = choose([0, 1, 2, 3, 4, 5], [2, 1.5, 1, 1, 1, 1]);
    let chords = [];

    // Relative to C: i=Cm, iiø=Dø, III=Eb, iv=Fm, V=G(7), VI=Ab, VII=Bb
    // rt.C=0, rt.D=2, rt.Ds=3, rt.F=5, rt.G=7, rt.Gs=8, rt.As=10
    switch (progType) {
        case 0: // i - iv - V - i (classic minor)
            chords.push(choose([
                [rt.C, tp.MinTriad], [rt.C, tp.Min7th], [rt.C, tp.MinFirstInv], [rt.C, tp.Min7thThirdInv],
            ]));
            chords.push(choose([
                [rt.F, tp.MinTriad], [rt.F, tp.Min7th], [rt.F, tp.MinFirstInv], [rt.F, tp.Min7thThirdInv],
            ]));
            chords.push(choose([
                [rt.G, tp.Dom7th], [rt.G, tp.Triad], [rt.G, tp.Dom7th9th], [rt.G, tp.Dom7thFlat9],
            ]));
            chords.push(choose([
                [rt.C, tp.MinTriad], [rt.C, tp.Min7th], [rt.C, tp.MinSecondInv],
            ]));
            break;

        case 1: // i - VI - III - VII (Andalusian / descending)
            chords.push(choose([[rt.C, tp.MinTriad], [rt.C, tp.Min7th], [rt.C, tp.MinFirstInv]]));
            chords.push(choose([[rt.Gs, tp.Maj7th], [rt.Gs, tp.Triad], [rt.Gs, tp.Maj6th]]));
            chords.push(choose([[rt.Ds, tp.Maj7th], [rt.Ds, tp.Triad], [rt.Ds, tp.Maj6th], [rt.Ds, tp.Maj7th9th]]));
            chords.push(choose([[rt.As, tp.Maj7th], [rt.As, tp.Triad], [rt.As, tp.Maj6th]]));
            break;

        case 2: // i - VII - VI - V
            chords.push(choose([[rt.C, tp.MinTriad], [rt.C, tp.Min7th]]));
            chords.push(choose([[rt.As, tp.Maj7th], [rt.As, tp.Triad]]));
            chords.push(choose([[rt.Gs, tp.Maj7th], [rt.Gs, tp.Triad], [rt.Gs, tp.Maj6th]]));
            chords.push(choose([[rt.G, tp.Dom7th], [rt.G, tp.Triad]]));
            break;

        case 3: // i - iv - VII - III (Dorian-leaning modal)
            chords.push(choose([[rt.C, tp.MinTriad], [rt.C, tp.Min7th]]));
            chords.push(choose([[rt.F, tp.MinTriad], [rt.F, tp.Min7th]]));
            chords.push(choose([[rt.As, tp.Maj7th], [rt.As, tp.Triad]]));
            chords.push(choose([[rt.Ds, tp.Maj7th], [rt.Ds, tp.Triad], [rt.Ds, tp.Maj6th]]));
            break;

        case 4: // ii°7 - V7 - i (minor jazz ii-V-i)
            chords.push(choose([[rt.D, tp.HalfDim], [rt.D, tp.Dim]]));
            chords.push(choose([[rt.G, tp.Dom7th], [rt.G, tp.Dom7thFlat9], [rt.G, tp.Dom7th9th]]));
            chords.push(choose([[rt.C, tp.MinTriad], [rt.C, tp.Min7th], [rt.C, tp.Min7th9th]]));
            chords.push(choose([[rt.C, tp.MinTriad], [rt.C, tp.Min7th]])); // hang on i
            break;

        case 5: // i - v - iv - i (modal vamp — no dominant)
            chords.push(choose([[rt.C, tp.MinTriad], [rt.C, tp.Min7th]]));
            chords.push(choose([[rt.G, tp.MinTriad], [rt.G, tp.Min7th]]));
            chords.push(choose([[rt.F, tp.MinTriad], [rt.F, tp.Min7th]]));
            chords.push(choose([[rt.C, tp.MinTriad], [rt.C, tp.Min7th]]));
            break;

        default:
            chords.push([rt.C, tp.MinTriad]);
            chords.push([rt.F, tp.MinTriad]);
            chords.push([rt.G, tp.Dom7th]);
            chords.push([rt.C, tp.MinTriad]);
    }
    chords._t = progType;
    return chords;
}

// Dorian progressions (minor-ish but with raised 6th → bittersweet, "Scarborough Fair" mood).
// Characteristic move: i - IV (major IV over a minor i).
function getDorianProg() {
    const progType = choose([0, 1, 2, 3, 4], [1.5, 1, 1, 1, 0.8]);
    const chords = [];
    switch (progType) {
        case 0: // i - IV vamp (classic Dorian)
            chords.push([rt.C, tp.Min7th]);
            chords.push([rt.F, tp.Maj7th]);
            chords.push([rt.C, tp.Min7th]);
            chords.push([rt.F, tp.Dom7th]);
            break;
        case 1: // i - VII - IV - i
            chords.push([rt.C, tp.MinTriad]);
            chords.push([rt.As, tp.Maj7th]);
            chords.push([rt.F, tp.Maj7th]);
            chords.push([rt.C, tp.Min7th]);
            break;
        case 2: // i - ii - IV - i (Dorian ii is minor)
            chords.push([rt.C, tp.Min7th]);
            chords.push([rt.D, tp.MinTriad]);
            chords.push([rt.F, tp.Maj7th]);
            chords.push([rt.C, tp.Min7th]);
            break;
        case 3: // ii - IV - i - v (long Dorian turnaround, v stays minor)
            chords.push([rt.D, tp.Min7th]);
            chords.push([rt.F, tp.Maj7th]);
            chords.push([rt.C, tp.Min7th]);
            chords.push([rt.G, tp.MinTriad]);
            break;
        case 4: // i - IV - v - IV (floating modal vamp, no dominant pull)
            chords.push([rt.C, tp.Min7th]);
            chords.push([rt.F, tp.Maj7th]);
            chords.push([rt.G, tp.MinTriad]);
            chords.push([rt.F, tp.Maj7th]);
            break;
        default:
            chords.push([rt.C, tp.Min7th]); chords.push([rt.F, tp.Maj7th]);
            chords.push([rt.C, tp.Min7th]); chords.push([rt.F, tp.Maj7th]);
    }
    chords._t = progType;
    return chords;
}

// Phrygian progressions — flat 2nd gives a dark/exotic character.
// Characteristic move: i - bII (e.g. Cm - Dbmaj).
function getPhrygianProg() {
    const progType = choose([0, 1, 2, 3, 4], [1.5, 1, 1, 1, 0.8]);
    const chords = [];
    switch (progType) {
        case 0: // i - bII - i vamp (Phrygian signature)
            chords.push([rt.C, tp.MinTriad]);
            chords.push([rt.Cs, tp.Maj7th]);
            chords.push([rt.C, tp.MinTriad]);
            chords.push([rt.Cs, tp.Triad]);
            break;
        case 1: // i - bVII - bII - i
            chords.push([rt.C, tp.Min7th]);
            chords.push([rt.As, tp.Maj7th]);
            chords.push([rt.Cs, tp.Maj7th]);
            chords.push([rt.C, tp.MinTriad]);
            break;
        case 2: // i - bII - bIII - i
            chords.push([rt.C, tp.MinTriad]);
            chords.push([rt.Cs, tp.Triad]);
            chords.push([rt.Ds, tp.Maj7th]);
            chords.push([rt.C, tp.MinTriad]);
            break;
        case 3: // i - iv - bII - i (slow dark descent)
            chords.push([rt.C, tp.Min7th]);
            chords.push([rt.F, tp.MinTriad]);
            chords.push([rt.Cs, tp.Maj7th]);
            chords.push([rt.C, tp.MinTriad]);
            break;
        case 4: // bIII - bII - i - bII (descending Andalusian-Phrygian hybrid)
            chords.push([rt.Ds, tp.Maj7th]);
            chords.push([rt.Cs, tp.Maj7th]);
            chords.push([rt.C, tp.MinTriad]);
            chords.push([rt.Cs, tp.Triad]);
            break;
        default:
            chords.push([rt.C, tp.MinTriad]); chords.push([rt.Cs, tp.Triad]);
            chords.push([rt.C, tp.MinTriad]); chords.push([rt.Cs, tp.Triad]);
    }
    chords._t = progType;
    return chords;
}

// Lydian progressions — raised 4th gives a dreamy/fantasy character.
// Characteristic move: I - II (major II, the "Simpsons" sound).
function getLydianProg() {
    const progType = choose([0, 1, 2, 3, 4], [1.5, 1, 1, 1, 0.8]);
    const chords = [];
    switch (progType) {
        case 0: // I - II vamp (Lydian signature)
            chords.push([rt.C, tp.Maj7th]);
            chords.push([rt.D, tp.Maj7th]);
            chords.push([rt.C, tp.Maj7th9th]);
            chords.push([rt.D, tp.Triad]);
            break;
        case 1: // I - II - vii - I
            chords.push([rt.C, tp.Maj7th]);
            chords.push([rt.D, tp.Triad]);
            chords.push([rt.B, tp.MinTriad]);
            chords.push([rt.C, tp.Maj7th]);
            break;
        case 2: // I - III - II - I (borrowed iii-minor for colour)
            chords.push([rt.C, tp.Maj7th]);
            chords.push([rt.E, tp.MinTriad]);
            chords.push([rt.D, tp.Maj7th]);
            chords.push([rt.C, tp.Maj7th]);
            break;
        case 3: // I#11 vamp (Lydian tonic with its signature extension)
            chords.push([rt.C, tp.Maj7Sharp11]);
            chords.push([rt.D, tp.Maj7th]);
            chords.push([rt.C, tp.Maj7Sharp11]);
            chords.push([rt.D, tp.Triad]);
            break;
        case 4: // I - vi - II - I (vi gives a warm minor dip before the bright II)
            chords.push([rt.C, tp.Maj7th]);
            chords.push([rt.A, tp.MinTriad]);
            chords.push([rt.D, tp.Maj7th]);
            chords.push([rt.C, tp.Maj7Sharp11]);
            break;
        default:
            chords.push([rt.C, tp.Maj7th]); chords.push([rt.D, tp.Maj7th]);
            chords.push([rt.C, tp.Maj7th]); chords.push([rt.D, tp.Maj7th]);
    }
    chords._t = progType;
    return chords;
}

// Mixolydian progressions — major with flat 7, bluesy/rock/folk character.
// Characteristic move: I - bVII (the "Hey Jude" / classic rock sound).
function getMixolydianProg() {
    const progType = choose([0, 1, 2, 3, 4], [1.5, 1, 1, 1, 0.8]);
    const chords = [];
    switch (progType) {
        case 0: // I - bVII vamp (classic rock / Beatles verse)
            chords.push([rt.C, tp.Dom7th]);
            chords.push([rt.As, tp.Maj7th]);
            chords.push([rt.C, tp.Triad]);
            chords.push([rt.As, tp.Triad]);
            break;
        case 1: // I - bVII - IV - I
            chords.push([rt.C, tp.Dom7th]);
            chords.push([rt.As, tp.Maj7th]);
            chords.push([rt.F, tp.Maj7th]);
            chords.push([rt.C, tp.Dom7th]);
            break;
        case 2: // I - v - IV - I (flat 7 makes v minor)
            chords.push([rt.C, tp.Dom7th]);
            chords.push([rt.G, tp.MinTriad]);
            chords.push([rt.F, tp.Maj7th]);
            chords.push([rt.C, tp.Dom7th]);
            break;
        case 3: // I - IV - bVII - IV (folk/Americana loop)
            chords.push([rt.C, tp.Triad]);
            chords.push([rt.F, tp.Maj7th]);
            chords.push([rt.As, tp.Maj7th]);
            chords.push([rt.F, tp.Triad]);
            break;
        case 4: // ii - IV - I - bVII (ii is minor in Mixolydian)
            chords.push([rt.D, tp.Min7th]);
            chords.push([rt.F, tp.Maj7th]);
            chords.push([rt.C, tp.Dom7th]);
            chords.push([rt.As, tp.Maj7th]);
            break;
        default:
            chords.push([rt.C, tp.Dom7th]); chords.push([rt.As, tp.Maj7th]);
            chords.push([rt.C, tp.Dom7th]); chords.push([rt.As, tp.Maj7th]);
    }
    chords._t = progType;
    return chords;
}

// Locrian progressions — unstable tonic (diminished). The "cursed" mode.
// No proper resolution — progressions orbit without landing. Use sparingly.
function getLocrianProg() {
    const progType = choose([0, 1, 2, 3, 4], [1.5, 1, 1, 1, 0.8]);
    const chords = [];
    switch (progType) {
        case 0: // i° - bII - i° (characteristic Locrian tension)
            chords.push([rt.C, tp.HalfDim]);
            chords.push([rt.Cs, tp.Maj7th]);
            chords.push([rt.C, tp.Dim]);
            chords.push([rt.Cs, tp.Triad]);
            break;
        case 1: // i° - iv - bII - i°
            chords.push([rt.C, tp.HalfDim]);
            chords.push([rt.F, tp.MinTriad]);
            chords.push([rt.Cs, tp.Maj7th]);
            chords.push([rt.C, tp.HalfDim]);
            break;
        case 2: // i° - bV - bII - i° (extra tension)
            chords.push([rt.C, tp.HalfDim]);
            chords.push([rt.Fs, tp.Maj7th]);
            chords.push([rt.Cs, tp.Maj7th]);
            chords.push([rt.C, tp.HalfDim]);
            break;
        case 3: // i° - bIII - bVII - i° (drifts through relative-major territory)
            chords.push([rt.C, tp.HalfDim]);
            chords.push([rt.Ds, tp.Maj7th]);
            chords.push([rt.As, tp.Triad]);
            chords.push([rt.C, tp.HalfDim]);
            break;
        case 4: // i° - bVI - bII - bVII (descending, never lands)
            chords.push([rt.C, tp.HalfDim]);
            chords.push([rt.Gs, tp.Maj7th]);
            chords.push([rt.Cs, tp.Maj7th]);
            chords.push([rt.As, tp.Triad]);
            break;
        default:
            chords.push([rt.C, tp.HalfDim]); chords.push([rt.Cs, tp.Maj7th]);
            chords.push([rt.C, tp.HalfDim]); chords.push([rt.Cs, tp.Maj7th]);
    }
    chords._t = progType;
    return chords;
}

// Helper: flatten [root, typeArr] pairs into absolute note arrays in the target key.
// Preserves the `_t` template tag attached to the chords array.
function flattenChords(chords, key) {
    const tag = chords._t;
    for (let i = 0; i < chords.length; i++) {
        const root = chords[i][0];
        const notes = chords[i][1];
        chords[i] = notes.map((x) => (x + root + key) % 12);
    }
    chords._t = tag;
    return chords;
}

function getProg(key, mode) {
    if (mode === 'minor')      return flattenChords(getMinorProg(key), key);
    if (mode === 'dorian')     return flattenChords(getDorianProg(), key);
    if (mode === 'phrygian')   return flattenChords(getPhrygianProg(), key);
    if (mode === 'lydian')     return flattenChords(getLydianProg(), key);
    if (mode === 'mixolydian') return flattenChords(getMixolydianProg(), key);
    if (mode === 'locrian')    return flattenChords(getLocrianProg(), key);
    const progType = choose(
        [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
        [1, 3, 1, 1, 0.5, 1, 1, 1, 0.8, 0.8],
    );
    let chords = [];
    let first;

    switch (progType) {
        case 0: // I IV I IV
            for (let i = 0; i < 2; i++) {
                chords.push(choose([
                    [rt.C, tp.Triad], [rt.C, tp.FirstInv], [rt.C, tp.SecondInv],
                    [rt.C, tp.Maj7th], [rt.C, tp.Maj6th],
                ]));
                chords.push(choose([
                    [rt.F, tp.Triad], [rt.F, tp.FirstInv], [rt.F, tp.SecondInv],
                    [rt.F, tp.Maj7th], [rt.F, tp.Maj7th9th], [rt.F, tp.Maj6th], [rt.F, tp.Add9],
                    [rt.G, tp.Triad], [rt.G, tp.FirstInv], [rt.G, tp.SecondInv],
                    [rt.G, tp.Maj6th], [rt.G, tp.Add9],
                ]));
            }
            if (chance(0.5)) { first = chords.shift(); chords.push(first); }
            break;

        case 1: // ii V I
            chords.push(choose([
                [rt.D, tp.MinTriad], [rt.D, tp.MinFirstInv], [rt.D, tp.MinSecondInv],
                [rt.D, tp.Min7th], [rt.D, tp.Min7th9th], [rt.D, tp.Min6th],
                [rt.F, tp.Triad], [rt.F, tp.FirstInv], [rt.F, tp.SecondInv],
                [rt.F, tp.Maj7th], [rt.F, tp.Maj7th9th], [rt.F, tp.Maj6th], [rt.F, tp.Add9],
            ]));
            chords.push(choose([
                [rt.G, tp.Triad], [rt.G, tp.FirstInv], [rt.G, tp.SecondInv],
                [rt.G, tp.Maj6th], [rt.G, tp.Dom7th], [rt.G, tp.Dom7th9th], [rt.G, tp.Add9],
                [rt.D, tp.Dim], [rt.D, tp.HalfDim],
                [rt.F, tp.Dim], [rt.F, tp.HalfDim],
                [rt.E, tp.MinTriad], [rt.E, tp.MinFirstInv], [rt.E, tp.MinSecondInv],
                [rt.E, tp.Min7th], [rt.E, tp.Min7th9th], [rt.E, tp.Min6th],
            ]));
            for (let i = 0; i < choose([1, 2]); i++) {
                chords.push(choose([
                    [rt.C, tp.Triad], [rt.C, tp.FirstInv], [rt.C, tp.SecondInv],
                    [rt.C, tp.Maj7th], [rt.C, tp.Maj7th9th], [rt.C, tp.Maj6th],
                ]));
            }
            break;

        case 2: // IV ii I
            chords.push(choose([
                [rt.F, tp.Triad], [rt.F, tp.FirstInv], [rt.F, tp.SecondInv],
                [rt.F, tp.Maj7th], [rt.F, tp.Maj7th9th], [rt.F, tp.Maj6th], [rt.F, tp.Add9],
            ]));
            chords.push(choose([
                [rt.D, tp.MinTriad], [rt.D, tp.MinFirstInv], [rt.D, tp.MinSecondInv],
                [rt.D, tp.Min7th], [rt.D, tp.Min7th9th], [rt.D, tp.Min6th],
            ]));
            for (let i = 0; i < choose([1, 2]); i++) {
                chords.push(choose([
                    [rt.C, tp.Triad], [rt.C, tp.FirstInv], [rt.C, tp.SecondInv],
                    [rt.C, tp.Maj7th], [rt.C, tp.Maj7th9th], [rt.C, tp.Maj6th],
                ]));
            }
            break;

        case 3: // IV I ii I
            chords.push(choose([
                [rt.F, tp.Triad], [rt.F, tp.FirstInv], [rt.F, tp.SecondInv],
                [rt.F, tp.Maj7th], [rt.F, tp.Maj7th9th], [rt.F, tp.Maj6th], [rt.F, tp.Add9],
            ]));
            chords.push(choose([
                [rt.C, tp.Triad], [rt.C, tp.FirstInv], [rt.C, tp.SecondInv],
                [rt.C, tp.Maj7th], [rt.C, tp.Maj7th9th], [rt.C, tp.Maj6th],
            ]));
            chords.push(choose([
                [rt.D, tp.MinTriad], [rt.D, tp.MinFirstInv], [rt.D, tp.MinSecondInv],
                [rt.D, tp.Min7th], [rt.D, tp.Min7th9th], [rt.D, tp.Min6th],
            ]));
            chords.push(choose([
                [rt.C, tp.Triad], [rt.C, tp.FirstInv], [rt.C, tp.SecondInv],
                [rt.C, tp.Maj7th], [rt.C, tp.Maj7th9th], [rt.C, tp.Maj6th],
            ]));
            if (chance(0.25)) { first = chords.shift(); chords.push(first); }
            break;

        case 4: // I iii ii iii
            chords.push(choose([
                [rt.C, tp.Triad], [rt.C, tp.FirstInv], [rt.C, tp.SecondInv],
                [rt.C, tp.Maj7th], [rt.C, tp.Maj7th9th], [rt.C, tp.Maj6th],
            ]));
            chords.push(choose([
                [rt.E, tp.MinTriad], [rt.E, tp.MinFirstInv], [rt.E, tp.MinSecondInv],
                [rt.E, tp.Min7th], [rt.E, tp.Min7th9th], [rt.E, tp.Min6th],
                [rt.G, tp.Triad], [rt.G, tp.FirstInv], [rt.G, tp.SecondInv], [rt.G, tp.Maj6th],
            ]));
            chords.push(choose([
                [rt.D, tp.MinTriad], [rt.D, tp.MinFirstInv], [rt.D, tp.MinSecondInv],
                [rt.D, tp.Min7th], [rt.D, tp.Min7th9th], [rt.D, tp.Min6th],
            ]));
            chords.push(choose([
                [rt.E, tp.MinTriad], [rt.E, tp.MinFirstInv], [rt.E, tp.MinSecondInv],
                [rt.E, tp.Min7th], [rt.E, tp.Min7th9th], [rt.E, tp.Min6th],
                [rt.G, tp.Triad], [rt.G, tp.FirstInv], [rt.G, tp.SecondInv], [rt.G, tp.Maj6th],
            ]));
            if (chance(0.5)) { first = chords.shift(); chords.push(first); }
            break;

        case 5: // IV (ii or iii) vi
            chords.push(choose([
                [rt.F, tp.Triad], [rt.F, tp.FirstInv], [rt.F, tp.SecondInv],
                [rt.F, tp.Maj7th], [rt.F, tp.Maj6th], [rt.F, tp.Add9],
            ]));
            chords.push(choose([
                [rt.D, tp.MinTriad], [rt.D, tp.MinFirstInv], [rt.D, tp.MinSecondInv],
                [rt.D, tp.Min7th], [rt.D, tp.Min7th9th], [rt.D, tp.Min6th],
                [rt.D, tp.Dim], [rt.D, tp.HalfDim],
                [rt.E, tp.MinTriad], [rt.E, tp.MinFirstInv], [rt.E, tp.MinSecondInv],
                [rt.E, tp.Min7th], [rt.E, tp.Min7th9th], [rt.E, tp.Min6th],
                [rt.F, tp.Dim], [rt.F, tp.HalfDim],
            ]));
            chords.push(choose([
                [rt.A, tp.MinTriad], [rt.A, tp.MinFirstInv], [rt.A, tp.MinSecondInv], [rt.A, tp.Min7th],
            ]));
            break;

        case 6: // (IV or ii) (Dim or V) iii vi
            chords.push(choose([
                [rt.F, tp.Triad], [rt.F, tp.FirstInv], [rt.F, tp.SecondInv],
                [rt.F, tp.Maj7th], [rt.F, tp.Maj6th], [rt.F, tp.Add9],
                [rt.D, tp.MinTriad], [rt.D, tp.MinFirstInv], [rt.D, tp.MinSecondInv],
                [rt.D, tp.Min7th], [rt.D, tp.Min7th9th], [rt.D, tp.Min6th],
            ]));
            if (chance(0.4)) {
                chords.push(choose([
                    [rt.G, tp.Triad], [rt.G, tp.FirstInv], [rt.G, tp.SecondInv],
                    [rt.G, tp.Maj6th], [rt.G, tp.Dom7th], [rt.G, tp.Dom7th9th], [rt.G, tp.Add9],
                ]));
                chords.push(choose([
                    [rt.E, tp.MinTriad], [rt.E, tp.MinFirstInv], [rt.E, tp.MinSecondInv],
                    [rt.E, tp.Min7th], [rt.E, tp.Min7th9th], [rt.E, tp.Min6th],
                ]));
            } else {
                chords.push(choose([
                    [rt.D, tp.Dim], [rt.D, tp.HalfDim],
                    [rt.F, tp.Dim], [rt.F, tp.HalfDim],
                ]));
                if (chance(0.5)) {
                    chords.push(choose([
                        [rt.E, tp.MinTriad], [rt.E, tp.MinFirstInv], [rt.E, tp.MinSecondInv],
                        [rt.E, tp.Min7th], [rt.E, tp.Min7th9th], [rt.E, tp.Min6th],
                    ]));
                }
            }
            chords.push(choose([
                [rt.A, tp.MinTriad], [rt.A, tp.MinFirstInv], [rt.A, tp.MinSecondInv], [rt.A, tp.Min7th],
            ]));
            break;

        case 7: // I△7#11 IV△7#11 — dreamy lydian-tinted vamp (2 chords × 2)
            for (let i = 0; i < 2; i++) {
                chords.push(choose([
                    [rt.C, tp.Maj7Sharp11], [rt.C, tp.Maj7th9th], [rt.C, tp.Maj7th], [rt.C, tp.Add9],
                ]));
                chords.push(choose([
                    [rt.F, tp.Maj7Sharp11], [rt.F, tp.Maj7th9th], [rt.F, tp.Maj7th], [rt.F, tp.Add9],
                ]));
            }
            if (chance(0.5)) { first = chords.shift(); chords.push(first); }
            break;

        case 8: // I vi IV V — 50s doo-wop / classic pop turnaround
            chords.push(choose([
                [rt.C, tp.Triad], [rt.C, tp.FirstInv],
                [rt.C, tp.Maj7th], [rt.C, tp.Maj7th9th], [rt.C, tp.Maj6th], [rt.C, tp.Add9],
            ]));
            chords.push(choose([
                [rt.A, tp.MinTriad], [rt.A, tp.MinFirstInv], [rt.A, tp.MinSecondInv],
                [rt.A, tp.Min7th], [rt.A, tp.Min7th9th], [rt.A, tp.Min6th],
            ]));
            chords.push(choose([
                [rt.F, tp.Triad], [rt.F, tp.FirstInv], [rt.F, tp.SecondInv],
                [rt.F, tp.Maj7th], [rt.F, tp.Maj7th9th], [rt.F, tp.Maj6th], [rt.F, tp.Add9],
            ]));
            chords.push(choose([
                [rt.G, tp.Triad], [rt.G, tp.FirstInv], [rt.G, tp.SecondInv],
                [rt.G, tp.Maj6th], [rt.G, tp.Dom7th], [rt.G, tp.Dom7th9th], [rt.G, tp.Add9],
            ]));
            if (chance(0.3)) { first = chords.shift(); chords.push(first); }
            break;

        case 9: // iii vi ii V — long jazz turnaround
            chords.push(choose([
                [rt.E, tp.MinTriad], [rt.E, tp.MinFirstInv], [rt.E, tp.MinSecondInv],
                [rt.E, tp.Min7th], [rt.E, tp.Min7th9th], [rt.E, tp.Min6th],
            ]));
            chords.push(choose([
                [rt.A, tp.MinTriad], [rt.A, tp.MinFirstInv], [rt.A, tp.MinSecondInv],
                [rt.A, tp.Min7th], [rt.A, tp.Min7th9th], [rt.A, tp.Min6th],
                [rt.A, tp.MinMaj7],
            ]));
            chords.push(choose([
                [rt.D, tp.MinTriad], [rt.D, tp.MinFirstInv], [rt.D, tp.MinSecondInv],
                [rt.D, tp.Min7th], [rt.D, tp.Min7th9th], [rt.D, tp.Min6th],
            ]));
            chords.push(choose([
                [rt.G, tp.Triad], [rt.G, tp.FirstInv], [rt.G, tp.SecondInv],
                [rt.G, tp.Maj6th], [rt.G, tp.Dom7th], [rt.G, tp.Dom7th9th], [rt.G, tp.Add9],
            ]));
            break;

        default:
            break;
    }

    // Reformat root-type pairs into absolute note arrays in the target key
    for (let i = 0; i < chords.length; i++) {
        const root = chords[i][0];
        const notes = chords[i][1];
        chords[i] = notes.map((x) => (x + root + key) % 12);
    }
    chords._t = progType;
    return chords;
}

// -------------------------------------------------------------------------
// Bass / voicings / rhythm
// -------------------------------------------------------------------------

function getBassSimple(prog, progRhythm, halfspeed) {
    const notes = prog.map((x) => Tone.Frequency(x[0] + 36, 'midi'));
    const bassline = [];
    const eighths = halfspeed ? 32 : 16;
    for (let i = 0; i < eighths; i++) bassline.push(0);
    let pos = 0;
    for (let i = 0; i < progRhythm.length; i++) {
        bassline[pos] = [notes[i], progRhythm[i]];
        pos += progRhythm[i].toSeconds() / Tone.Time('8n').toSeconds();
    }
    return bassline;
}

function getBassRhythmic(prog, progRhythm, halfspeed) {
    const notes = prog.map((x) => Tone.Frequency(x[0] + 36, 'midi'));

    let low = 0;
    let high = 0;
    for (let i = 1; i < notes.length; i++) {
        if (notes[i].toMidi() < notes[low].toMidi()) low = i;
        if (notes[i].toMidi() > notes[high].toMidi()) high = i;
    }

    let transposeUp = true;
    if (notes[high].toMidi() > 45) transposeUp = false;

    const bassline = [];
    for (let i = 0; i < progRhythm.length; i++) {
        const chordEighths = Math.round(progRhythm[i].toSeconds() / Tone.Time('8n').toSeconds());
        let pos = 0;
        let notePlayed = false;
        while (pos < chordEighths) {
            let dur;
            if (chance(0.7) || Boolean(!notePlayed && (chordEighths - pos) < 3)) {
                notePlayed = true;
                dur = choose([1, 2, 3, 4, 6, 8], [0.5, 1.5, 0.5, 1, 0.5, 0.5]);
                dur = dur > (chordEighths - pos) ? chordEighths - pos : dur;
                let note = notes[i];
                if (i === low && transposeUp) {
                    if (note.toMidi() < 38 && chance(0.5)) note = note.transpose(12);
                } else if (i === high && !transposeUp) {
                    if (chance(0.5)) note = note.transpose(-12);
                }
                bassline.push([note, Tone.Time(dur * Tone.Time('8n').toSeconds())]);
                for (let j = 0; j < dur - 1; j++) bassline.push(null);
            } else {
                dur = (chordEighths - pos) > 1 ? choose([1, 2]) : 1;
                for (let j = 0; j < dur; j++) bassline.push(null);
            }
            pos += dur;
        }
    }
    // halfspeed parameter kept for signature parity
    return bassline;
}

function getProgVoiced(prog, progRhythm, halfspeed) {
    const voicings = getVoicings(prog);
    const progVoiced = [];
    const eighths = halfspeed ? 32 : 16;
    for (let i = 0; i < eighths; i++) progVoiced.push(0);
    let pos = 0;
    for (let i = 0; i < progRhythm.length; i++) {
        progVoiced[pos] = [voicings[i], progRhythm[i]];
        pos += Math.round(progRhythm[i].toSeconds() / Tone.Time('8n').toSeconds());
    }
    return progVoiced;
}

function getVoicings(originalProg) {
    const newProg = [];
    for (let i = 0; i < originalProg.length; i++) newProg.push([...originalProg[i]]);

    const maxSpacing = Math.floor(between(16, 28));
    const spacer = Math.floor(maxSpacing / 8);

    // If the chord has only 3 notes, add the octave above the lowest
    for (let i = 0; i < newProg.length; i++) {
        if (newProg[i].length === 3) newProg[i].push(newProg[i][0] + 12);
    }

    for (let i = 0; i < newProg.length; i++) {
        let spaced = false;
        while (!spaced) {
            spaced = true;
            newProg[i].sort((a, b) => a - b);
            for (let j = 1; j < newProg[i].length; j++) {
                if (newProg[i][j] - newProg[i][j - 1] < spacer) {
                    newProg[i][j] += 12;
                    spaced = false;
                }
            }
        }
        let compressed = false;
        while (!compressed) {
            compressed = true;
            for (let j = 1; j < newProg[i].length; j++) {
                if (newProg[i][j] - newProg[i][0] > maxSpacing) {
                    newProg[i][j] -= 12;
                    compressed = false;
                }
            }
        }
    }
    return newProg.map((x) => x.map((y) => Tone.Frequency(y + 60, 'midi')));
}

function getProgRhythm(numChords, halfspeed) {
    const double = [
        ['2n', '2n'],
        ['4n', '2n.'],
        ['2n.', '4n'],
    ];
    const triple = [
        ['2n', '4n', '4n'],
        ['4n', '2n', '4n'],
    ];

    let rhythm;
    if (numChords === 3) {
        const doub = choose(double, [0.6, 0.2, 0.2]);
        rhythm = choose([
            ['1m'].concat(doub),
            doub.concat(['1m']),
        ]);
    } else if (numChords === 4) {
        rhythm = choose([
            ['1m'].concat(choose(triple)),
            choose(triple).concat(['1m']),
            choose(double, [0.6, 0.2, 0.2]).concat(choose(double, [0.6, 0.2, 0.2])),
        ], [0.2, 0.2, 0.6]);
    } else {
        // Fallback — shouldn't happen with the 7 prog templates, but be safe.
        rhythm = Array(numChords).fill('1m');
    }

    if (halfspeed) return rhythm.map((x) => Tone.Time(Tone.Time(x).toSeconds() * 2));
    return rhythm.map((x) => Tone.Time(x));
}

// -------------------------------------------------------------------------
// Melody + harmony
// -------------------------------------------------------------------------

function getMelody(key, prog, progRhythm, halfspeed, mode) {
    const scaleBase = MODE_SCALES[mode] || MAJOR_SCALE;
    const scale = scaleBase.map((x) => (x + key) % 12);
    const rangeRand = Math.min(key + Math.floor(between(12, 25)), 30);
    const range = [rangeRand, rangeRand + 16];
    let phrase = [];
    const phrase2 = [];

    for (let i = 0; i < progRhythm.length; i++) {
        const chordEighths = Math.round(progRhythm[i].toSeconds() / Tone.Time('8n').toSeconds());
        const chord = prog[i];
        const numMelodyNotes = halfspeed ? chordEighths : chordEighths * 2;
        for (let j = 0; j < numMelodyNotes; j++) {
            const chosen = chooseNote(phrase, scale, chord, range, Boolean(j === 0));
            phrase.push(chosen);
            if (i < progRhythm.length - 1) {
                phrase2.push(chosen);
            } else {
                phrase2.push(chooseNote(phrase2, scale, chord, range, Boolean(j === 0)));
            }
        }
    }

    phrase = phrase.concat(phrase2);
    if (halfspeed) {
        for (let i = phrase.length - 1; i >= 0; i--) phrase.splice(i, 0, phrase[i]);
    }

    const formatted = [];
    let prev = null;
    for (let i = 0; i < phrase.length; i += 2) {
        if (phrase[i] === prev || phrase[i] == null) {
            if (phrase[i] === phrase[i + 1] || phrase[i + 1] == null) {
                formatted.push(null);
            } else {
                const note = Tone.Frequency(phrase[i + 1] + 48, 'midi');
                const dur = getMelodyNoteDuration(phrase, i + 1, halfspeed);
                formatted.push([null, [note, dur]]);
            }
        } else {
            if (phrase[i] === phrase[i + 1] || phrase[i + 1] == null) {
                const note = Tone.Frequency(phrase[i] + 48, 'midi');
                const dur = getMelodyNoteDuration(phrase, i, halfspeed);
                formatted.push([[note, dur], null]);
            } else {
                const firstNote = Tone.Frequency(phrase[i] + 48, 'midi');
                const firstDur = halfspeed ? Tone.Time('8n') : Tone.Time('16n');
                const secondNote = Tone.Frequency(phrase[i + 1] + 48, 'midi');
                const secondDur = getMelodyNoteDuration(phrase, i + 1, halfspeed);
                formatted.push([[firstNote, firstDur], [secondNote, secondDur]]);
            }
        }
        prev = phrase[i + 1];
    }
    return formatted;
}

function getMelodyNoteDuration(phrase, pos, halfspeed) {
    let dur = 1;
    let end = false;
    while (!end) {
        if (pos === phrase.length) {
            end = true;
        } else {
            pos++;
            if (phrase[pos] === phrase[pos + 1]) dur++;
            else end = true;
        }
    }
    dur = halfspeed ? dur * 2 : dur;
    return Tone.Time(dur * Tone.Time('16n').toSeconds());
}

function getHarmoniousNotes(scale, chord, intervals = [0, 2, 3, 4, 7, 9, 10, 11]) {
    const harmonious = [...chord];
    for (let i = 0; i < chord.length; i++) {
        if (!scale.includes(chord[i])) return harmonious;
    }
    const possibilities = intervals.map((x) => (x + chord[0]) % 12);
    for (let i = 0; i < possibilities.length; i++) {
        if (scale.includes(possibilities[i]) && !harmonious.includes(possibilities[i])) {
            harmonious.push(possibilities[i]);
        }
    }
    return harmonious;
}

function chooseNote(phrase, scale, chord, range, newChord) {
    const lastSixteenth = phrase.length > 0 ? phrase[phrase.length - 1] : null;
    let lastNote = lastSixteenth;
    if (lastNote === null) {
        for (let i = phrase.length; i > 0; i--) {
            if (phrase[i - 1]) { lastNote = phrase[i - 1]; break; }
        }
    }

    let sustained = 0;
    for (let i = phrase.length; i > 0; i--) {
        if (phrase[i - 1] === lastSixteenth) sustained++;
        else break;
    }

    const harmonious = getHarmoniousNotes(scale, chord);
    const mostHarmonious = getHarmoniousNotes(scale, chord, [0, 3, 4, 7]);
    const quarterBeat = phrase.length % 4 === 0;

    let changeChance = phrase.length % 2 === 0 ? 0.15 : 0.05;
    changeChance += quarterBeat ? 0.1 : 0;
    changeChance += newChord ? 0.2 : 0;
    changeChance += sustained / 32;
    changeChance = Math.min(changeChance, 1);

    let firstProb; let restProb; let repeatProb; let stepProb; let leapProb;

    if (lastNote) {
        firstProb = 0;
        if (lastSixteenth) {
            restProb = changeChance / 8;
            repeatProb = harmonious.includes(lastSixteenth % 12) ? 1 - changeChance : 0.25 * (1 - changeChance);
            stepProb = changeChance / 2;
            leapProb = changeChance / 8;
        } else {
            restProb = (1 - changeChance) / 3;
            repeatProb = harmonious.includes(lastNote % 12) ? changeChance / 3 : 0;
            stepProb = changeChance / 2;
            leapProb = changeChance / 8;
        }
    } else {
        firstProb = Math.min(changeChance * 5, 1);
        repeatProb = 0;
        stepProb = 0;
        leapProb = 0;
        restProb = 1 - firstProb;
    }

    const probabilities = [firstProb, repeatProb, stepProb, leapProb, restProb];
    const action = choose(['first', 'repeat', 'step', 'leap', 'rest'], probabilities);
    let note;
    let allowable; let options;

    switch (action) {
        case 'first':
            note = range[0] + Math.floor(between(3, 13));
            { const dir = choose([-1, 1]);
              while (!chord.includes(note % 12)) note += dir; }
            break;
        case 'repeat':
            note = lastNote;
            break;
        case 'step':
            allowable = quarterBeat ? [...mostHarmonious] : [...harmonious];
            options = [];
            for (let i = 1; i <= 2; i++) {
                if (allowable.includes((lastNote + i) % 12) && (lastNote + i) <= range[1]) options.push(lastNote + i);
                if (allowable.includes((lastNote - i) % 12) && (lastNote - i) >= range[0]) options.push(lastNote - i);
            }
            if (options.length > 0) { note = choose(options); break; }
            // fallthrough to leap
        // eslint-disable-next-line no-fallthrough
        case 'leap':
            allowable = quarterBeat ? [...mostHarmonious] : [...harmonious];
            options = [];
            for (let i = 0; i < allowable.length; i++) {
                for (let j = 0; j < 6; j++) {
                    const candidate = allowable[i] + (12 * j);
                    if (candidate <= range[1] && candidate >= range[0]) {
                        const dist = Math.abs(lastNote - candidate);
                        if (dist < 13 && dist > 2 && dist !== 6) options.push(candidate);
                    }
                }
            }
            if (options.length > 0) { note = choose(options); break; }
            // fallthrough to rest
        // eslint-disable-next-line no-fallthrough
        case 'rest':
        default:
            note = null;
            break;
    }
    return note;
}

function getHarmony(key, prog, progRhythm, halfspeed, melody, mode) {
    const scaleBase = MODE_SCALES[mode] || MAJOR_SCALE;
    const scale = scaleBase.map((x) => (x + key) % 12);
    const lower = chance(0.7);

    const chordIndices = [];
    for (let i = 0; i < progRhythm.length; i++) {
        const chordEighths = Math.round(progRhythm[i].toSeconds() / Tone.Time('8n').toSeconds());
        for (let j = 0; j < chordEighths; j++) chordIndices.push(i);
    }

    const harmony = [];
    for (let i = 0; i < melody.length; i++) {
        if (!melody[i]) {
            harmony.push(null);
        } else {
            const eighth = [];
            const chord = prog[chordIndices[i % chordIndices.length]];
            for (let j = 0; j < melody[i].length; j++) {
                if (!melody[i][j]) {
                    eighth.push(null);
                } else {
                    const sixteenth = [...melody[i][j]];
                    const melodyNote = sixteenth[0].toMidi();
                    const harmonious = getHarmoniousNotes(scale, chord, [0, 3, 4, 7]);
                    let options = [];
                    for (let k = -9; k <= 9; k++) {
                        const candidate = melodyNote + k;
                        if (harmonious.includes(candidate % 12)) {
                            const dist = Math.abs(melodyNote - candidate);
                            if (dist > 2 && dist !== 6) options.push(candidate);
                        }
                    }
                    if (options.length >= 2) {
                        options = lower ? options.slice(0, 2) : options.slice(-2, options.length);
                    }
                    const note = options.length > 0 ? choose(options) : choose([melodyNote + 12, melodyNote - 12]);
                    sixteenth[0] = Tone.Frequency(note, 'midi');
                    eighth.push(sixteenth);
                }
            }
            harmony.push(eighth);
        }
    }
    return harmony;
}

// -------------------------------------------------------------------------
// Drum patterns + fills
// -------------------------------------------------------------------------

function getKickPattern() {
    return [
        1, 0, 0, 0,
        choose([0, 1], [0.8, 0.2]), choose([0, 1]), 0, choose([0, 1], [0.8, 0.2]),
        1, 0, 0, 0,
        choose([0, 1], [0.8, 0.2]), choose([0, 1]), 0, choose([0, 1], [0.8, 0.2]),
    ];
}

function getSnarePattern() {
    return [
        0, 0, 1, 0, 0, 0, 1, choose([0, 1], [0.9, 0.1]),
        0, 0, 1, 0, 0, 0, 1, choose([0, 1], [0.9, 0.1]),
    ];
}

function getHatPattern() {
    if (chance(0.5)) {
        return [
            choose([0, 1], [0.1, 0.9]), choose([0, 1], [0.1, 0.9]),
            choose([0, 1], [0.1, 0.9]), choose([0, 1], [0.1, 0.9]),
            choose([0, 1], [0.1, 0.9]), choose([0, 1], [0.1, 0.9]),
            choose([0, 1], [0.1, 0.9]), choose([0, 1], [0.2, 0.8]),
            choose([0, 1], [0.1, 0.9]), choose([0, 1], [0.1, 0.9]),
            choose([0, 1], [0.1, 0.9]), choose([0, 1], [0.1, 0.9]),
            choose([0, 1], [0.1, 0.9]), choose([0, 1], [0.1, 0.9]),
            choose([0, 1], [0.1, 0.9]), choose([0, 1], [0.2, 0.8]),
        ];
    }
    return [
        1, 0, 1, 0, 1, 0, 1, choose([0, 1]),
        1, 0, 1, 0, 1, 0, 1, choose([0, 1]),
    ];
}

function buildFillEighths(accentIndices) {
    const fill = [];
    for (let i = 0; i < 8; i++) {
        let chosen;
        if (fill.length === 0) {
            chosen = choose([[0, 1], [1, 0], [0, 0]], [0.2, 0.5, 0.3]);
        } else {
            const last = fill[fill.length - 1];
            let numNotes = 0;
            for (let j = 0; j < fill.length; j++) if (fill[j][0] || fill[j][1]) numNotes++;
            const chanceBoost = accentIndices.includes(i) ? 1 : 0;
            if (!last[0] && !last[1]) {
                chosen = choose([[0, 1], [1, 0], [0, 0]], [1 + chanceBoost, 2 + chanceBoost, 2 + numNotes]);
            } else if (last[1]) {
                chosen = [0, 0];
            } else {
                chosen = choose([[0, 1], [1, 0], [0, 0]], [1 + chanceBoost, 1 + chanceBoost, 2 + numNotes]);
            }
        }
        fill.push(chosen);
    }
    return fill;
}

function getKickFill(kickPattern, shortFill) {
    let fill = buildFillEighths([4, 7]);
    let regularEighths = [...kickPattern];
    if (shortFill) {
        regularEighths = regularEighths.concat(regularEighths.slice(0, 12));
        fill = fill.slice(4);
    } else {
        regularEighths = regularEighths.concat(regularEighths.slice(0, 8));
    }
    const regular = regularEighths.map((x) => [x, 0]);
    return regular.concat(fill);
}

function getSnareFill(snarePattern, shortFill) {
    let fill = buildFillEighths([2, 6]);
    let regularEighths = [...snarePattern];
    if (shortFill) {
        regularEighths = regularEighths.concat(regularEighths.slice(0, 12));
        fill = fill.slice(4);
    } else {
        regularEighths = regularEighths.concat(regularEighths.slice(0, 8));
    }
    const regular = regularEighths.map((x) => [x, 0]);
    return regular.concat(fill);
}

function getHatFill(hatPattern, shortFill) {
    let fill = [];
    let sparsePattern = true;
    for (let i = 1; i < 6; i += 2) {
        if (hatPattern[i]) { sparsePattern = false; break; }
    }

    if (sparsePattern) {
        for (let i = 0; i < 8; i++) {
            fill.push(choose([[0, 1], [1, 0], [0, 0]]));
        }
    } else {
        for (let i = 0; i < 8; i++) {
            fill.push(choose([[0, 1], [1, 0], [0, 0], [1, 1]], [1, 1, 1, 0.8]));
        }
    }

    let regularEighths = [...hatPattern];
    if (shortFill) {
        regularEighths = regularEighths.concat(regularEighths.slice(0, 12));
        fill = fill.slice(4);
    } else {
        regularEighths = regularEighths.concat(regularEighths.slice(0, 8));
    }
    const regular = regularEighths.map((x) => [x, 0]);
    return regular.concat(fill);
}

// -------------------------------------------------------------------------
// Sample loaders — Ambience (Tone.Player) + Instrument (Tone.Sampler)
// -------------------------------------------------------------------------

function getAudioBufferFromFilepath(filepath) {
    return new Promise((resolve, reject) => {
        fetch(filepath)
            .then((response) => response.arrayBuffer())
            .then((arrayBuffer) => Tone.getContext().decodeAudioData(arrayBuffer))
            .then((audioBuffer) => resolve(audioBuffer))
            .catch(reject);
    });
}

function checkBuffersLoaded(map) {
    // A slot is considered "done" when it's been replaced with an AudioBuffer
    // OR null (meaning the fetch failed). Strings mean still-loading URLs.
    for (const note in map) {
        if (typeof map[note] === 'string') return false;
    }
    return true;
}

class Ambience {
    constructor(playerConstructor) {
        this.player = null;
        this.isLoaded = false;
        this.playerConstructor = playerConstructor;
    }

    load() {
        return new Promise((resolve) => {
            if (this.isLoaded) { resolve(); return; }
            const filepath = this.playerConstructor.url;
            getAudioBufferFromFilepath(filepath)
                .then((buffer) => {
                    this.playerConstructor.url = buffer;
                    this.isLoaded = true;
                    resolve();
                })
                .catch((err) => {
                    // Don't block the whole engine on a missing ambience file.
                    // eslint-disable-next-line no-console
                    console.warn('Failed to load ambience sample', filepath, err);
                    this.isLoaded = true;
                    resolve();
                });
        });
    }

    create() {
        return new Promise((resolve) => {
            if (!this.isLoaded) {
                this.load().then(() => {
                    try {
                        this.player = new Tone.Player(this.playerConstructor);
                    } catch (e) {
                        // buffer never resolved — create a silent placeholder
                        this.player = null;
                    }
                    resolve(this.player);
                });
            } else {
                try {
                    this.player = new Tone.Player(this.playerConstructor);
                } catch (e) {
                    this.player = null;
                }
                resolve(this.player);
            }
        });
    }

    connect(node) {
        return new Promise((resolve) => {
            if (!this.player) {
                this.create().then((player) => {
                    if (player) resolve(player.connect(node));
                    else resolve(null);
                });
            } else {
                resolve(this.player.connect(node));
            }
        });
    }
}

class Instrument {
    constructor(samplerConstructor, vol, attack, release) {
        this.sampler = null;
        this.isLoaded = false;
        this.samplerConstructor = samplerConstructor;
        this.vol = vol;
        this.attack = attack;
        this.release = release;
    }

    // Use Tone.Sampler's built-in loader — much simpler than doing our own
    // fetch/decode pipeline. Resolves when Tone says all samples are loaded.
    load() {
        return new Promise((resolve) => {
            if (this.sampler) { this.isLoaded = true; resolve(); return; }
            try {
                this.sampler = new Tone.Sampler({
                    urls: this.samplerConstructor.urls,
                    baseUrl: this.samplerConstructor.baseUrl,
                    release: this.release,
                    attack: this.attack,
                    volume: this.vol,
                    onload: () => {
                        this.isLoaded = true;
                        resolve();
                    },
                    onerror: (err) => {
                        // Partial load is still useful; resolve anyway.
                        // eslint-disable-next-line no-console
                        console.warn('Sampler partial load error:', err.message || err);
                        this.isLoaded = true;
                        resolve();
                    },
                });
            } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('Failed to build Sampler', e);
                this.sampler = null;
                this.isLoaded = false;
                resolve();
            }
        });
    }

    create() { return this.load().then(() => this.sampler); }

    connect(node) {
        return new Promise((resolve) => {
            if (!this.sampler) {
                this.load().then(() => {
                    if (this.sampler) resolve(this.sampler.connect(node));
                    else resolve(null);
                });
            } else {
                resolve(this.sampler.connect(node));
            }
        });
    }
}

// -------------------------------------------------------------------------
// Section scheduler — returns a callback that fires each eighth beat and
// dispatches to an array of part callbacks.
// -------------------------------------------------------------------------

function getSection(song, parts) {
    return function (now) {
        now += 0.15;
        const pos = song.transport.position.split(':').map((x) => parseInt(x, 10));
        const eighths = Math.floor((pos[0] * 8) + (pos[1] * 2) + (pos[2] / 2));
        for (let i = 0; i < parts.length; i++) parts[i](now, eighths, song);
    };
}

// -------------------------------------------------------------------------
// The LofigenEngine class
// -------------------------------------------------------------------------

export class LofigenEngine {
    constructor(options = {}) {
        this.baseUrl = options.baseUrl || SAMPLE_BASE;
        this.ext = null; // decided once Tone is available

        // Effects chain — created in init().
        this.gain = null;
        this.vibratoSlow = null;
        this.vibratoFast = null;
        this.lowpass = null;
        this.filter = null;
        this.chordalGain = null;
        this.chordalVerb = null;
        this.chordalEcho = null;
        this.chordalChorus = null;
        this.chordalFilter = null;
        this.leadGain = null;
        this.leadVerb = null;
        this.leadEcho = null;
        this.leadChorus = null;
        this.leadFilter = null;
        this.bassGain = null;
        this.limiter = null;
        this.finalGain = null;

        // Instruments
        this.piano0 = null;
        this.piano1 = null;
        this.rhodes0 = null;
        this.rhodes1 = null;
        this.synth0 = null;
        this.kalimba = null;
        this.dantranh = null;
        this.marimba = null;
        this.guitar = null;
        this.uke = null;
        this.violinpizz = null;
        this.bass0 = null;
        this.bass1 = null;
        this.bass2 = null;
        this.bass3 = null;
        this.hat = null;
        this.snare = null;
        this.kick = null;

        // Atmosphere
        this.ambiences = [];
        this.vinyls = [];
        this.artifacts = [];
        this.intros = [];

        // Runtime state
        this.song = null;
        this._starting = null;
        this._autoSkip = true;
        this._started = false;
        this._inited = false;
    }

    // Load all samples. Safe to call multiple times — second call is a no-op.
    async init() {
        if (this._inited) return;
        this._inited = true;

        this.ext = Tone.ToneAudioBuffer.supportsType('webm') ? 'webm' : 'aac';
        this._initNodes();
        this._buildInstruments();

        // Eagerly load everything the engine could pick from. This fires off
        // ~18 parallel fetches; we await the combined promise before returning.
        const allInstruments = [
            this.piano0, this.piano1, this.rhodes0, this.rhodes1,
            this.synth0, this.kalimba, this.dantranh, this.marimba,
            this.guitar, this.uke, this.violinpizz,
            this.bass0, this.bass1, this.bass2, this.bass3,
            this.hat, this.snare, this.kick,
        ];
        await Promise.all(allInstruments.map((inst) => inst.load()));

        // Ambience + vinyl are small; load lazily but fire them too.
        await Promise.all(this.ambiences.map((a) => a.load()));
        await Promise.all(this.vinyls.map((v) => v.load()));
        await Promise.all(this.artifacts.map((a) => a.load()));
        await Promise.all(this.intros.map((a) => a.load()));
    }

    // Begin playback. Resumes audio context and generates + plays a song.
    async start() {
        if (!this._inited) await this.init();
        await Tone.start();
        this._autoSkip = true;
        this._onStart();
    }

    // Halt everything and cancel scheduled events.
    stop() {
        this._autoSkip = false;
        for (let i = 0; i < this.ambiences.length; i++) {
            const a = this.ambiences[i];
            if (a.player && a.player.state === 'started') a.player.stop();
        }
        for (let i = 0; i < this.vinyls.length; i++) {
            const v = this.vinyls[i];
            if (v.player && v.player.state === 'started') v.player.stop();
        }
        for (let i = 0; i < this.artifacts.length; i++) {
            const a = this.artifacts[i];
            if (a.player && a.player.state === 'started') a.player.stop();
        }
        for (let i = 0; i < this.intros.length; i++) {
            const a = this.intros[i];
            if (a.player && a.player.state === 'started') a.player.stop();
        }
        if (this.filter) this.filter.frequency.cancelScheduledValues(Tone.now());
        Tone.Transport.cancel();
        if (Tone.Transport.state !== 'stopped') Tone.Transport.stop();
        this._started = false;
    }

    // Skip to a fresh track.
    skip() {
        this.stop();
        clearTimeout(this._starting);
        this._autoSkip = true;
        this._starting = setTimeout(() => this._onStart(), 500);
    }

    // Introspection for UI — what's playing right now?
    getCurrentTrack() {
        if (!this.song) return null;
        return {
            bpm: this.song.bpm,
            key: this.song.key,
            mode: this.song.mode || 'major',
            isMinor: !!this.song.isMinor,
            halfspeed: this.song.halfspeedProg,
            // prog is an array of note-number arrays (0..11 modulo key)
            progression: this.song.prog,
            progTemplate: this.song.prog?._t,
            ambienceName: this.song.ambienceName,
            vinylName: this.song.vinylName,
            artifacts: this.song.artifacts || [],
            introName: this.song.introName,
            instruments: {
                chordal: this._instrumentName(this.song.chordal),
                lead: this._instrumentName(this.song.lead),
                bass: this._instrumentName(this.song.bass),
            },
        };
    }

    // Cleanup — call when the UI unmounts.
    dispose() {
        this.stop();
        clearTimeout(this._starting);
        // Let Tone GC the nodes. Being exhaustive so the audio graph can be rebuilt.
        const nodes = [
            this.gain, this.vibratoSlow, this.vibratoFast, this.lowpass, this.filter,
            this.chordalGain, this.chordalVerb, this.chordalEcho, this.chordalChorus, this.chordalFilter,
            this.leadGain, this.leadVerb, this.leadEcho, this.leadChorus, this.leadFilter,
            this.bassGain, this.limiter, this.finalGain,
        ];
        nodes.forEach((n) => { try { if (n && n.dispose) n.dispose(); } catch (e) { /* ignore */ } });
    }

    // ---- Internal helpers ----

    _instrumentName(sampler) {
        if (!sampler) return null;
        const map = [
            [this.piano0 && this.piano0.sampler, 'piano_kawai'],
            [this.piano1 && this.piano1.sampler, 'piano_upright'],
            [this.rhodes0 && this.rhodes0.sampler, 'rhodes0'],
            [this.rhodes1 && this.rhodes1.sampler, 'rhodes1'],
            [this.synth0 && this.synth0.sampler, 'synth'],
            [this.kalimba && this.kalimba.sampler, 'kalimba'],
            [this.dantranh && this.dantranh.sampler, 'dantranh'],
            [this.marimba && this.marimba.sampler, 'marimba'],
            [this.guitar && this.guitar.sampler, 'guitar'],
            [this.uke && this.uke.sampler, 'uke'],
            [this.violinpizz && this.violinpizz.sampler, 'violin_pizz'],
            [this.bass0 && this.bass0.sampler, 'bass'],
            [this.bass1 && this.bass1.sampler, 'contrabass'],
            [this.bass2 && this.bass2.sampler, 'synthbass'],
            [this.bass3 && this.bass3.sampler, 'guitar_low'],
        ];
        for (let i = 0; i < map.length; i++) if (map[i][0] === sampler) return map[i][1];
        return 'unknown';
    }

    _initNodes() {
        this.limiter = new Tone.Limiter(-0.8).toDestination();
        this.finalGain = new Tone.Gain(3, 'decibels').connect(this.limiter);
        this.gain = new Tone.Gain(1).connect(this.finalGain);
        this.vibratoSlow = new Tone.Vibrato(0.5, 0.2).connect(this.gain);
        this.vibratoFast = new Tone.Vibrato(7, 0.06).connect(this.vibratoSlow);
        this.lowpass = new Tone.Filter(20000, 'lowpass').connect(this.vibratoFast);
        this.filter = new Tone.Filter(20, 'highpass').connect(this.lowpass);

        this.chordalGain = new Tone.Gain(1).connect(this.filter);
        this.chordalVerb = new Tone.Reverb(1).connect(this.chordalGain);
        this.chordalEcho = new Tone.FeedbackDelay('8n', 0.2).connect(this.chordalVerb);
        this.chordalChorus = new Tone.Chorus(4, 2.5, 0.5).connect(this.chordalEcho).start();
        this.chordalFilter = new Tone.Filter(20, 'highpass').connect(this.chordalChorus);

        this.leadGain = new Tone.Gain(1).connect(this.filter);
        this.leadVerb = new Tone.Reverb(1).connect(this.leadGain);
        this.leadEcho = new Tone.FeedbackDelay('8n', 0.2).connect(this.leadVerb);
        this.leadChorus = new Tone.Chorus(4, 2.5, 0.5).connect(this.leadEcho).start();
        this.leadFilter = new Tone.Filter(20, 'highpass').connect(this.leadChorus);

        this.bassGain = new Tone.Gain(1).connect(this.filter);

        // Artifact FX chain — the "muffled signal bleeding through from
        // another room" processing. Anything routed through here comes out
        // sounding like a memory rather than a citation. Chain order:
        //
        //   artifact player
        //     → artifactLowpass (kills "here and now" high frequencies)
        //     → artifactReverb (puts the sound in a different space)
        //     → artifactGain (sits behind the mix)
        //     → filter (joins the master chain, gets tape-wobble etc.)
        //
        // Filter cutoff is intentionally fixed at a muffled value; per-fire
        // randomisation can happen later if we want more variation.
        this.artifactGain = new Tone.Gain(1).connect(this.filter);
        this.artifactReverb = new Tone.Reverb(1.2).connect(this.artifactGain);
        this.artifactReverb.wet.value = 0.45;
        this.artifactLowpass = new Tone.Filter(2200, 'lowpass').connect(this.artifactReverb);
        // Mild resonance for a slightly "bloomed" speaker-cone character.
        this.artifactLowpass.Q.value = 0.7;

        // Intro FX chain — lighter than artifacts. Intros need to be *felt*
        // (thunder should rumble, radio should crackle with texture) rather
        // than *half-remembered*. Same reverb for unity, but much less
        // aggressive lowpass so the full body of the sample comes through.
        this.introGain = new Tone.Gain(1).connect(this.filter);
        this.introReverb = new Tone.Reverb(1.8).connect(this.introGain);
        this.introReverb.wet.value = 0.22;
        this.introLowpass = new Tone.Filter(6000, 'lowpass').connect(this.introReverb);
        this.introLowpass.Q.value = 0.5;
    }

    _buildInstruments() {
        const ext = this.ext;
        const base = this.baseUrl;

        this.piano0 = new Instrument({
            urls: {
                'A1': 'A1.' + ext, 'A2': 'A2.' + ext, 'A6': 'A6.' + ext, 'A7': 'A7.' + ext,
                'A#0': 'As0.' + ext, 'A#3': 'As3.' + ext, 'A#4': 'As4.' + ext, 'A#5': 'As5.' + ext,
                'C1': 'C1.' + ext, 'C2': 'C2.' + ext, 'C3': 'C3.' + ext, 'C4': 'C4.' + ext,
                'C5': 'C5.' + ext, 'C6': 'C6.' + ext, 'C7': 'C7.' + ext, 'C8': 'C8.' + ext,
                'D3': 'D3.' + ext, 'D4': 'D4.' + ext, 'D5': 'D5.' + ext,
                'D#1': 'Ds1.' + ext, 'D#2': 'Ds2.' + ext, 'D#6': 'Ds6.' + ext, 'D#7': 'Ds7.' + ext,
                'E3': 'E3.' + ext, 'E4': 'E4.' + ext, 'E5': 'E5.' + ext,
                'F#1': 'Fs1.' + ext, 'F#2': 'Fs2.' + ext, 'F#3': 'Fs3.' + ext, 'F#4': 'Fs4.' + ext,
                'F#5': 'Fs5.' + ext, 'F#6': 'Fs6.' + ext, 'F#7': 'Fs7.' + ext,
                'G7': 'G7.' + ext, 'G#3': 'Gs3.' + ext, 'G#4': 'Gs4.' + ext, 'G#5': 'Gs5.' + ext,
            },
            baseUrl: base + 'piano_kawai/',
        }, 10, 0, 0.1);

        this.piano1 = new Instrument({
            urls: {
                'A2': 'A2.' + ext, 'A3': 'A3.' + ext, 'A4': 'A4.' + ext, 'A5': 'A5.' + ext,
                'A6': 'A6.' + ext, 'A7': 'A7.' + ext,
                'C#1': 'Cs1.' + ext, 'C#2': 'Cs2.' + ext, 'C#3': 'Cs3.' + ext, 'C#4': 'Cs4.' + ext,
                'C#5': 'Cs5.' + ext, 'C#6': 'Cs6.' + ext, 'C#7': 'Cs7.' + ext,
                'C8': 'C8.' + ext,
                'F1': 'F1.' + ext, 'F2': 'F2.' + ext, 'F3': 'F3.' + ext, 'F4': 'F4.' + ext,
                'F5': 'F5.' + ext, 'F6': 'F6.' + ext, 'F7': 'F7.' + ext,
            },
            baseUrl: base + 'piano_upright/',
        }, 2, 0, 0.3);

        this.rhodes0 = new Instrument({
            urls: {
                'C2': 'C2.' + ext, 'C3': 'C3.' + ext, 'C4': 'C4.' + ext, 'C5': 'C5.' + ext,
                'E2': 'E2.' + ext, 'E3': 'E3.' + ext, 'E4': 'E4.' + ext,
                'G2': 'G2.' + ext, 'G3': 'G3.' + ext, 'G4': 'G4.' + ext, 'G5': 'G5.' + ext,
            },
            baseUrl: base + 'rhodes0/',
        }, -20, 0, 0.3);

        this.rhodes1 = new Instrument({
            urls: {
                'C2': 'C2.' + ext, 'C3': 'C3.' + ext, 'C4': 'C4.' + ext, 'C5': 'C5.' + ext,
                'C6': 'C6.' + ext, 'C7': 'C7.' + ext,
                'E1': 'E1.' + ext, 'E2': 'E2.' + ext, 'E3': 'E3.' + ext, 'E4': 'E4.' + ext,
                'E5': 'E5.' + ext, 'E6': 'E6.' + ext, 'E7': 'E7.' + ext,
                'G#1': 'Gs1.' + ext, 'G#2': 'Gs2.' + ext, 'G#3': 'Gs3.' + ext, 'G#4': 'Gs4.' + ext,
                'G#5': 'Gs5.' + ext, 'G#6': 'Gs6.' + ext,
            },
            baseUrl: base + 'rhodes1/',
        }, -18, 0, 0.3);

        this.synth0 = new Instrument({
            urls: {
                'C#2': 'Cs1.' + ext, 'F#2': 'Fs1.' + ext, 'B2': 'B1.' + ext,
                'E3': 'E2.' + ext, 'A3': 'A2.' + ext, 'D4': 'D3.' + ext, 'G4': 'G3.' + ext,
                'C5': 'C4.' + ext, 'F5': 'F4.' + ext, 'A#5': 'As4.' + ext, 'D#6': 'Ds5.' + ext,
                'G#6': 'Gs5.' + ext, 'C#7': 'Cs6.' + ext,
            },
            baseUrl: base + 'synth/',
        }, -12, 0, 0.1);

        this.kalimba = new Instrument({
            urls: {
                'C4': 'C4.' + ext, 'D4': 'D4.' + ext, 'F#4': 'Fs4.' + ext, 'A4': 'A4.' + ext,
                'C5': 'C5.' + ext, 'E5': 'E5.' + ext, 'G5': 'G5.' + ext, 'B5': 'B5.' + ext,
            },
            baseUrl: base + 'kalimba/',
        }, -12, 0, 2);

        this.dantranh = new Instrument({
            urls: {
                'B2': 'B2.' + ext, 'B3': 'B3.' + ext, 'B4': 'B4.' + ext, 'B5': 'B5.' + ext,
                'D#3': 'Ds3.' + ext, 'D#4': 'Ds4.' + ext, 'D#5': 'Ds5.' + ext,
                'F#3': 'Fs3.' + ext, 'F#4': 'Fs4.' + ext, 'F#5': 'Fs5.' + ext,
                'G#3': 'Gs3.' + ext, 'G#4': 'Gs4.' + ext, 'G#5': 'Gs5.' + ext,
            },
            baseUrl: base + 'dantranh/',
        }, 6, 0, 2);

        this.marimba = new Instrument({
            urls: {
                'B3': 'B2.' + ext, 'B5': 'B4.' + ext, 'C3': 'C2.' + ext, 'C5': 'C4.' + ext,
                'C7': 'C6.' + ext, 'F2': 'F1.' + ext, 'F4': 'F3.' + ext, 'F7': 'F5.' + ext,
                'G3': 'G2.' + ext, 'G5': 'G4.' + ext,
            },
            baseUrl: base + 'marimba/',
        }, 0, 0, 2);

        this.guitar = new Instrument({
            urls: {
                'A3': 'A3.' + ext, 'A4': 'A4.' + ext, 'C5': 'C5.' + ext,
                'E4': 'E4.' + ext, 'E5': 'E5.' + ext, 'G#5': 'Gs5.' + ext,
            },
            baseUrl: base + 'guitar/',
        }, 5, 0, 0.5);

        this.uke = new Instrument({
            urls: {
                'A4': 'A4.' + ext, 'A#5': 'As5.' + ext, 'D5': 'D5.' + ext,
                'E4': 'E4.' + ext, 'G5': 'G5.' + ext,
            },
            baseUrl: base + 'uke/',
        }, 4, 0, 0.5);

        this.violinpizz = new Instrument({
            urls: {
                'A3': 'A3.' + ext, 'A4': 'A4.' + ext, 'A5': 'A5.' + ext,
                'C4': 'C4.' + ext, 'C5': 'C5.' + ext,
                'E4': 'E4.' + ext, 'E5': 'E5.' + ext,
                'G3': 'G3.' + ext, 'G4': 'G4.' + ext, 'G5': 'G5.' + ext,
            },
            baseUrl: base + 'violin_pizz/',
        }, 5, 0, 0.5);

        this.bass0 = new Instrument({
            urls: {
                'C#2': 'Cs1.' + ext, 'F#2': 'Fs1.' + ext, 'B2': 'B1.' + ext,
                'E3': 'E2.' + ext, 'A3': 'A2.' + ext, 'D4': 'D3.' + ext, 'G4': 'G3.' + ext,
                'C5': 'C4.' + ext, 'F5': 'F4.' + ext, 'A#5': 'As4.' + ext, 'D#6': 'Ds5.' + ext,
                'G#6': 'Gs5.' + ext, 'C#7': 'Cs6.' + ext,
            },
            baseUrl: base + 'bass/',
        }, -12, 0, 0.1);

        this.bass1 = new Instrument({
            urls: {
                'A#1': 'As1.' + ext, 'A2': 'A2.' + ext, 'B3': 'B3.' + ext,
                'C2': 'C2.' + ext, 'C#3': 'Cs3.' + ext, 'D2': 'D2.' + ext,
                'E1': 'E1.' + ext, 'E2': 'E2.' + ext, 'E3': 'E3.' + ext,
                'F#1': 'Fs1.' + ext, 'F#2': 'Fs2.' + ext,
                'G1': 'G1.' + ext, 'G#2': 'Gs2.' + ext, 'G#3': 'Gs3.' + ext,
            },
            baseUrl: base + 'contrabass/',
        }, 8, 0, 0.1);

        this.bass2 = new Instrument({
            urls: {
                'D2': 'D1.' + ext, 'F#2': 'Fs1.' + ext, 'A#2': 'As1.' + ext, 'D3': 'D2.' + ext,
            },
            baseUrl: base + 'synthbass/',
        }, -10, 0, 0.1);

        this.bass3 = new Instrument({
            urls: {
                'A2': 'A2.' + ext, 'C2': 'C2.' + ext, 'C3': 'C3.' + ext,
                'D#2': 'Ds2.' + ext, 'F2': 'F2.' + ext,
            },
            baseUrl: base + 'guitar_low/',
        }, -10, 0, 0.1);

        this.hat = new Instrument({
            urls: {
                'C1': 'hat0.' + ext, 'D1': 'hat1.' + ext, 'E1': 'hat2.' + ext,
                'F#1': 'hat3.' + ext, 'G#1': 'hat4.' + ext, 'A#1': 'hat5.' + ext,
                'C2': 'shaker0.' + ext,
            },
            baseUrl: base + 'hat/',
        }, -24, 0, 0);

        this.snare = new Instrument({
            urls: {
                'C1': 'snare0.' + ext, 'D1': 'snare1.' + ext, 'E1': 'snare2.' + ext,
                'F#1': 'snare3.' + ext, 'G#1': 'snare4.' + ext, 'A#1': 'snare5.' + ext,
                'C2': 'snare6.' + ext, 'D2': 'snare7.' + ext, 'E2': 'snare6.' + ext,
                'F#2': 'snare7.' + ext, 'G#2': 'snare8.' + ext, 'A#2': 'snare9.' + ext,
                'C3': 'snare10.' + ext, 'D3': 'snare11.' + ext, 'E3': 'snare12.' + ext,
                'F#3': 'snare13.' + ext, 'G#3': 'snare14.' + ext, 'A#3': 'snare15.' + ext,
                'C4': 'snare16.' + ext, 'D4': 'snare17.' + ext, 'E4': 'snare18.' + ext,
                'F#4': 'snare19.' + ext, 'G#4': 'snare20.' + ext,
            },
            baseUrl: base + 'snare/',
        }, 0, 0, 0);

        this.kick = new Instrument({
            urls: {
                'C1': 'kick0.' + ext, 'D1': 'kick1.' + ext, 'E1': 'kick2.' + ext,
                'F#1': 'kick3.' + ext, 'G#1': 'kick4.' + ext, 'A#1': 'kick5.' + ext,
                'C2': 'kick6.' + ext, 'D2': 'kick7.' + ext, 'E2': 'kick8.' + ext,
                'F#2': 'kick9.' + ext, 'G#2': 'kick10.' + ext, 'A#2': 'kick11.' + ext,
                'C3': 'kick12.' + ext, 'D3': 'kick13.' + ext, 'E3': 'kick14.' + ext,
                'F#3': 'kick15.' + ext, 'G#3': 'kick16.' + ext, 'A#3': 'kick17.' + ext,
                'C4': 'kick18.' + ext, 'D4': 'kick19.' + ext,
            },
            baseUrl: base + 'kick/',
        }, 0, 0, 0);

        // Ambience and vinyl beds.
        // The pool is Dylan's original 7 ambience + 4 vinyl, supplemented with
        // Thingdoms noise samples from the old drum pack (characterful radios,
        // old TVs, rain, road, stormy TV, extra vinyls). Engine picks one
        // ambience + one vinyl per track; more files = less repetition.
        this.ambiences = [];
        this.vinyls = [];

        const AMBIENCE_FILES = [
            // Dylan's originals — generic atmospheres.
            'ambience0', 'ambience1', 'ambience2', 'ambience3',
            'ambience4', 'ambience5', 'ambience6',
            // Thingdoms additions from the drum pack — characterful textures.
            'old_radio', 'old_tv', 'radio', 'radio2', 'rain', 'road', 'stormy_tv',
            // Long-form field recordings — each plays once through the track
            // without looping (samples longer than a track's duration).
            'birds_chirping', 'rain_on_roof', 'rain_window_thunder', 'traffic_manila',
        ];
        const VINYL_FILES = [
            // Dylan's originals — surface noise / crackle.
            'vinyl0', 'vinyl1', 'vinyl2', 'vinyl3',
            // Thingdoms additions — extra crackle variants.
            'dusty_vinyl', 'static_vinyl',
        ];

        for (let i = 0; i < AMBIENCE_FILES.length; i++) {
            this.ambiences.push(new Ambience({
                url: base + 'ambience/' + AMBIENCE_FILES[i] + '.' + ext,
                loop: true,
                fadein: 2,
                volume: -14,
                name: AMBIENCE_FILES[i],
            }));
        }
        for (let i = 0; i < VINYL_FILES.length; i++) {
            this.vinyls.push(new Ambience({
                url: base + 'ambience/' + VINYL_FILES[i] + '.' + ext,
                loop: true,
                fadein: 2,
                volume: -16,
                name: VINYL_FILES[i],
            }));
        }

        // Artifacts — one-shot atmospheric samples (birds, radios, distant
        // sounds, etc.) that fire at random moments during a track. Unlike
        // ambience/vinyl (continuous beds), artifacts are incidental: a
        // sparrow, a thunder rumble, a coffee pour — one hears them and then
        // they're gone. Philosophy: evocative of our world, never referential
        // to specific external IP. See ARTIFACT_FILES comment below.
        this.artifacts = [];

        // Edit this list when you add files to public/samples/lofigen/artifacts/
        // All samples run through the muffled-distant FX chain before output,
        // so specific-IP samples (Zelda, Mario, PS1, etc.) come out sounding
        // like distant radio signals rather than direct citations.
        const ARTIFACT_FILES = [
            // Tech nostalgia
            'dial_up_sound', 'ps1_bootup',
            // Zelda
            'zelda_chest', 'zelda_heart', 'zelda_secret', 'zelda_secret_unlocked',
            'zelda_navi_listen', 'zelda_song_of_time', 'zelda_flute',
            'zelda_fairy1', 'zelda_fairy2',
            'zelda_link1', 'zelda_link2', 'zelda_link3', 'zelda_link4',
            'zelda_navi1', 'zelda_navi2', 'zelda_navi3', 'zelda_navi4', 'zelda_navi5',
            // Mario
            'mario_1up', 'mario_billfirework', 'mario_coin', 'mario_gameover',
            'mario_jump', 'mario_jumpsmall', 'mario_pipepowerdown', 'mario_powerup',
            // Pokémon — cries, jingles, UI
            'pokemon_squirtle_cry', 'pokemon_chansey', 'pokemon_mew', 'pokemon_snorlax',
            'pokemon_a_button', 'pokemon_level_up_short', 'pokemon_level_up_long',
            'pokemon_low_hp',
            // Pokémon voice clips — borderline, may feel too referential after muffling
            'pokemon_voice_backing_down', 'pokemon_voice_next', 'pokemon_voice_hallucinate',
            'pokemon_voice_reserves', 'pokemon_voice_swap', 'pokemon_voice_full_out',
            'pokemon_voice_wrong_choice',
            // Long voice clips (10+s) — intentionally tried despite risk of dominating
            'pokemon_voice_showdown', 'pokemon_voice_lt_surge',
            // Star Wars
            'star_wars_dark_forces', 'star_wars_lightsaber', 'star_wars_chosen_one',
            'star_wars_i_love_you',
            // Misc cultural
            'sims_hello', 'brilliant_thanks',
            // Street Fighter
            'street_fighter_lose', 'street_fighter_tuesday',
            // Simpsons / sitcom voice clips
            'voice_spilled_sundae', 'voice_obese_trapped', 'voice_you_buzzed',
            'voice_real_fast', 'voice_car_sleeping', 'voice_scooby_do_it',
            'voice_vibrations_feet', 'voice_idea',
            // Long voice clip — another swing at a 20+s bleedthrough
            'voice_amanda_huggenkiss',
            // Austin Powers
            'voice_introduce_myself', 'voice_frickin_bone', 'voice_how_bout_no',
            'voice_zip_it_good', 'voice_zip_it_short', 'voice_coffee_shit',
            'voice_dr_evil_hug', 'voice_where_dr_evil', 'voice_the_dutch',
            // Pulp Fiction
            'voice_tasty_burger', 'voice_royale_cheese', 'voice_5_shake',
            'voice_say_what_again',
            // Toy Story
            'voice_found_spaceship', 'voice_spaceship_buzz', 'voice_buzz_lightyear',
            // Inglourious Basterds / Blazing Saddles
            'voice_thats_a_bingo', 'voice_lili_von_shtupp',
            // Misc one-liners
            'voice_hit_the_spot', 'voice_box_of_these',
            // Misc
            'razbarry_death_scream',
        ];

        for (let i = 0; i < ARTIFACT_FILES.length; i++) {
            this.artifacts.push(new Ambience({
                url: base + 'artifacts/' + ARTIFACT_FILES[i] + '.' + ext,
                loop: false,
                fadein: 0.05,
                volume: -12,
                name: ARTIFACT_FILES[i],
            }));
        }

        // Intros — longer one-shots that fire at t=0 and fade out naturally.
        // Set the scene (thunder rolling in, radio tuning, phone ringing).
        // ~40% of tracks get one.
        const INTRO_FILES = [
            'thunder_long', 'thunder_short',
            'phone_ringing', 'phone_ringing_tone',
            'crickets', 'car_crickets',
            'radio_signal_voice', 'radio_morse', 'radio_chatter',
            'car_starting',
            'dial_up_modem',
            'youre_implying',
            'voice_sweetest_thing',
            'pizza_planet_dinner', 'pizza_planet_boarding',
            'slurp',  // fine to curate out later if not working
        ];

        for (let i = 0; i < INTRO_FILES.length; i++) {
            this.intros.push(new Ambience({
                url: base + 'intros/' + INTRO_FILES[i] + '.' + ext,
                loop: false,
                fadein: 0.1,
                volume: -4,
                name: INTRO_FILES[i],
            }));
        }
    }

    _connectKit() {
        if (this.hat && this.hat.sampler) this.hat.sampler.connect(this.filter);
        if (this.kick && this.kick.sampler) this.kick.sampler.connect(this.filter);
        if (this.snare && this.snare.sampler) this.snare.sampler.connect(this.filter);
    }

    _chooseVinyl() {
        return new Promise((resolve) => {
            if (this.vinyls.length === 0) { resolve({ player: null, name: null }); return; }
            const index = Math.floor(between(0, this.vinyls.length));
            const name = this.vinyls[index].playerConstructor?.name || null;
            this.vinyls[index].connect(this.gain)
                .then((player) => {
                    if (!player) { resolve({ player: null, name }); return; }
                    player.playbackRate = between(0.7, 1);
                    player.volume.value = between(-19, -16);
                    resolve({ player, name });
                });
        });
    }

    _chooseAmbience() {
        return new Promise((resolve) => {
            if (this.ambiences.length === 0) { resolve({ player: null, name: null }); return; }
            const index = Math.floor(between(0, this.ambiences.length));
            const name = this.ambiences[index].playerConstructor?.name || null;
            this.ambiences[index].connect(this.gain)
                .then((player) => {
                    if (!player) { resolve({ player: null, name }); return; }
                    player.playbackRate = between(0.9, 1);
                    player.volume.value = between(-18, -14);
                    resolve({ player, name });
                });
        });
    }

    // Pick at most one intro for this track. Fires at Transport bar 0 and
    // fades out over bar 3-4 so it's silent as drums arrive at bar 4. The
    // musical structure already has chord pad (no drums) for bars 0-4 and
    // full drums from bar 4 onward, so the intro naturally aligns with the
    // pre-drum "scene setting" section.
    _chooseIntro() {
        return new Promise(async (resolve) => {
            if (this.intros.length === 0) { resolve(null); return; }
            if (!chance(0.4)) { resolve(null); return; }

            const index = Math.floor(between(0, this.intros.length));
            const art = this.intros[index];
            const name = art.playerConstructor?.name || null;

            const player = await art.connect(this.introLowpass).catch(() => null);
            if (!player) { resolve(null); return; }

            // Intros are elevated (felt, not remembered).
            player.volume.value = between(-6, -2);
            player.playbackRate = 1;

            // Start at bar 0, full volume.
            Tone.Transport.scheduleOnce((time) => {
                try {
                    if (player && player.buffer && player.buffer.loaded) {
                        player.start(time);
                    }
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.warn('Intro failed to fire', name, e);
                }
            }, 0);

            // Fade out over bars 3-4 so drums enter cleanly. One-bar ramp
            // from current volume to -60 dB, then stop the player to free
            // the node.
            Tone.Transport.scheduleOnce((time) => {
                try {
                    if (!player || !player.buffer || !player.buffer.loaded) return;
                    const bpm = Tone.Transport.bpm.value;
                    const secondsPerBar = (60 / bpm) * 4;
                    const fadeDuration = secondsPerBar; // 1 bar fade
                    player.volume.cancelScheduledValues(time);
                    player.volume.setValueAtTime(player.volume.value, time);
                    player.volume.linearRampToValueAtTime(-60, time + fadeDuration);
                    player.stop(time + fadeDuration + 0.05);
                } catch (e) {
                    // Fade automation can fail on some Tone builds — harmless.
                }
            }, '3:0:0');

            resolve({ name });
        });
    }

    // Pick 0-3 artifact samples for this track and schedule them to fire at
    // random moments. Weighted so ~30% of tracks are artifact-free — artifacts
    // should feel like occasional incidents, not a mandatory layer.
    //
    // Returns an array of { name, fireAtSeconds } descriptors for UI display;
    // actual firing is scheduled via Tone.Transport below.
    _chooseArtifacts() {
        return new Promise(async (resolve) => {
            if (this.artifacts.length === 0) { resolve([]); return; }

            // How many artifacts should fire in this track?
            //   0 → 30%, 1 → 40%, 2 → 20%, 3 → 10%
            const count = choose([0, 1, 2, 3], [3, 4, 2, 1]);
            if (count === 0) { resolve([]); return; }

            // Pick `count` unique artifacts.
            const pool = this.artifacts.map((_, i) => i);
            const picked = [];
            for (let i = 0; i < count && pool.length > 0; i++) {
                const poolIdx = Math.floor(between(0, pool.length));
                picked.push(pool[poolIdx]);
                pool.splice(poolIdx, 1);
            }

            // Compute fire times with guaranteed spacing. Divide the
            // 15s–80s window into `count` equal slots, then jitter within
            // each slot so no two artifacts can land on top of each other.
            // With count=3: ~21s min gap. With count=2: ~32s min gap.
            const windowStart = 15;
            const windowEnd = 80;
            const slotSize = (windowEnd - windowStart) / picked.length;
            // Jitter is slightly inset so artifacts never fire right at slot
            // boundaries (which would still feel mechanical).
            const jitterPad = Math.min(slotSize * 0.15, 2);
            const fireTimes = picked.map((_, i) => {
                const slotStart = windowStart + i * slotSize + jitterPad;
                const slotEnd   = windowStart + (i + 1) * slotSize - jitterPad;
                return between(slotStart, slotEnd);
            });

            const descriptors = [];
            for (let i = 0; i < picked.length; i++) {
                const index = picked[i];
                const art = this.artifacts[index];
                const name = art.playerConstructor?.name || null;

                // Connect this artifact's player through the muffled-distant
                // FX chain so it comes out sounding like a memory / distant
                // radio signal rather than a clean present-day sample.
                // eslint-disable-next-line no-await-in-loop
                const player = await art.connect(this.artifactLowpass).catch(() => null);
                if (!player) continue;

                // Tight volume range — the source files are loudness-normalised
                // to -16 LUFS so runtime variation only needs to be subtle.
                player.volume.value = between(-11, -8);
                player.playbackRate = between(0.92, 1.08);

                const fireAtSeconds = fireTimes[i];

                // Schedule via Tone.Transport so stop() cancels cleanly.
                Tone.Transport.scheduleOnce((time) => {
                    try {
                        if (player && player.buffer && player.buffer.loaded) {
                            player.start(time);
                        }
                    } catch (e) {
                        // eslint-disable-next-line no-console
                        console.warn('Artifact failed to fire', name, e);
                    }
                }, fireAtSeconds);

                descriptors.push({ name, fireAtSeconds: Math.round(fireAtSeconds) });
            }

            resolve(descriptors);
        });
    }

    _chooseChordal() {
        const options = [this.rhodes1, this.rhodes0, this.piano0, this.piano1,
                         this.kalimba, this.synth0, this.marimba];
        const chordal = choose(options);
        return chordal.connect(this.chordalFilter);
    }

    _chooseLead() {
        const options = [this.rhodes1, this.rhodes0, this.piano1, this.kalimba,
                         this.synth0, this.dantranh, this.marimba,
                         this.violinpizz, this.guitar, this.uke];
        const lead = choose(options);
        return lead.connect(this.leadFilter);
    }

    _chooseBass(ducking, bpm) {
        let bassOptions;
        if (ducking) bassOptions = [this.bass2, this.rhodes1, this.bass3];
        else bassOptions = bpm < 75 ? [this.bass1, this.bass3] : [this.bass0, this.bass1, this.bass3];
        const bass = choose(bassOptions);
        return bass.connect(this.bassGain);
    }

    _setEffects() {
        setScalarOrSignal(this.leadVerb, 'decay', between(0.5, 2.5));
        this.leadVerb.wet.value = between(0.1, 0.5);
        this.leadEcho.delayTime.value = Tone.Time(choose(['8n', '4n'])).toSeconds();
        this.leadEcho.feedback.value = between(0.1, 0.3);
        this.leadEcho.wet.value = choose([0, between(0, 0.2)]);
        this.leadChorus.frequency.value = choose([2, 4]);
        setScalarOrSignal(this.leadChorus, 'depth', between(0, 0.3));
        if (this.leadChorus.delayTime && this.leadChorus.delayTime.value !== undefined) {
            this.leadChorus.delayTime.value = between(2, 2.5) / 1000;
        }
        this.leadChorus.wet.value = choose([0, between(0, 1)]);
        this.leadFilter.frequency.value = choose([20, between(300, 1200)], [0.8, 0.2]);

        setScalarOrSignal(this.chordalVerb, 'decay', between(0.5, 2.5));
        this.chordalVerb.wet.value = between(0.1, 0.65);
        this.chordalEcho.delayTime.value = Tone.Time(choose(['8n', '4n'])).toSeconds();
        this.chordalEcho.feedback.value = between(0.1, 0.3);
        this.chordalEcho.wet.value = choose([0, between(0, 0.2)]);
        this.chordalChorus.frequency.value = choose([2, 4]);
        setScalarOrSignal(this.chordalChorus, 'depth', between(0, 0.3));
        if (this.chordalChorus.delayTime && this.chordalChorus.delayTime.value !== undefined) {
            this.chordalChorus.delayTime.value = between(2, 2.5) / 1000;
        }
        this.chordalChorus.wet.value = choose([0, between(0, 1)]);
        this.chordalFilter.frequency.value = choose([20, between(200, 500)], [0.8, 0.2]);

        this.vibratoSlow.depth.value = between(0, 0.25);
        this.vibratoFast.depth.value = between(0, 0.1);
        this.lowpass.frequency.value = choose(
            [20000, between(6000, 10000), between(10000, 14000)],
            [0.5, 0.2, 0.3],
        );
    }

    _duck(now, bassDucking) {
        const attack = 0.008;
        const min = 0.015849;
        const release = 0.44;
        const sustain = 0.06;
        this.chordalGain.gain.rampTo(min, attack, now);
        this.chordalGain.gain.rampTo(1, release, now + attack + sustain);
        if (bassDucking) {
            this.bassGain.gain.rampTo(min, attack, now);
            this.bassGain.gain.rampTo(1, release, now + attack + sustain);
        }
    }

    _setFilter(filterType, now) {
        switch (filterType) {
            case 0:
                this.filter.type = 'highpass';
                this.filter.frequency.setValueAtTime(20, now);
                break;
            case 1:
                this.filter.type = 'lowpass';
                this.filter.frequency.setValueAtTime(between(300, 400), now);
                break;
            case 2:
                this.filter.type = 'highpass';
                this.filter.frequency.setValueAtTime(between(1000, 1600), now);
                break;
            default:
                break;
        }
    }

    _rampAddFilter(now, rampTime) {
        this.filter.type = chance(0.5) ? 'lowpass' : 'highpass';
        if (this.filter.type === 'highpass') {
            this.filter.frequency.setValueAtTime(20, now);
            this.filter.frequency.rampTo(between(1000, 1600), rampTime, now);
        } else if (this.filter.type === 'lowpass') {
            this.filter.frequency.setValueAtTime(20000, now);
            this.filter.frequency.rampTo(between(600, 1000), rampTime, now);
        }
    }

    _rampRemoveFilter(now, rampTime) {
        if (this.filter.type === 'highpass') this.filter.frequency.rampTo(20, rampTime, now);
        else if (this.filter.type === 'lowpass') this.filter.frequency.rampTo(20000, rampTime, now);
    }

    _fadeOut(now) {
        this.gain.gain.rampTo(0, '6:0:0', now);
    }

    _onStart() {
        const transport = Tone.Transport;
        // Fresh gain level every track
        if (this.gain) this.gain.gain.setValueAtTime(1, Tone.now());
        const song = this._getSong(transport);
        this.song = song;
        this._startPlaying(song);
    }

    _startPlaying(song) {
        const check = () => {
            const sounds = [
                this.kick && this.kick.sampler,
                this.snare && this.snare.sampler,
                this.hat && this.hat.sampler,
                song.chordal, song.bass, song.lead,
                song.ambience, song.vinyl,
            ];
            for (let i = 0; i < sounds.length; i++) {
                if (!sounds[i]) return false;
                if (sounds[i].loaded === false) return false;
            }
            return true;
        };
        if (!check()) {
            this._startingPoll = setTimeout(() => this._startPlaying(song), 50);
            return;
        }
        song.transport.start();
        this._started = true;
    }

    _getSong(transport, bpm) {
        const song = {};
        song.offline = false;
        song.transport = transport;

        song.bpm = typeof bpm === 'number' ? bpm : between(60, 90);
        transport.bpm.value = song.bpm;
        transport.swing = choose([between(0, 0.02), between(0.02, 0.1)], [0.8, 0.2]);
        song.key = Math.floor(between(0, 12));
        // Thingdoms extension: pick a mode. Weighted — major is still most
        // common but minor + modal variants bring real expressive range.
        // TEMP (eval mode): all 7 modes equally likely so we can sample them.
        // Previous weighted distribution — restore later:
        //   ['major','minor','dorian','phrygian','lydian','mixolydian','locrian']
        //   [5,       3,      1.5,     1,         1.5,     1.8,         0.4]
        song.mode = choose(
            ['major', 'minor', 'dorian', 'phrygian', 'lydian', 'mixolydian', 'locrian'],
            [1,       1,       1,        1,          1,        1,            1],
        );
        song.isMinor = song.mode !== 'major' && song.mode !== 'lydian' && song.mode !== 'mixolydian';

        // Kick off async chooses. They race against _startPlaying's loaded-check.
        this._chooseAmbience().then((res) => { song.ambience = res.player; song.ambienceName = res.name; });
        this._chooseVinyl().then((res) => { song.vinyl = res.player; song.vinylName = res.name; });
        this._chooseArtifacts().then((descriptors) => { song.artifacts = descriptors; });
        this._chooseIntro().then((res) => {
            song.introName = res ? res.name : null;
        });
        this._chooseChordal().then((res) => { song.chordal = res; });
        this._chooseLead().then((res) => { song.lead = res; });

        song.bassDucking = chance(0.7);
        this._chooseBass(song.bassDucking, song.bpm).then((res) => { song.bass = res; });

        this._connectKit();
        this._setEffects();

        song.prog = getProg(song.key, song.mode);
        song.halfspeedProg = song.bpm < 70 ? false : chance(0.3);
        song.progRhythm = getProgRhythm(song.prog.length, song.halfspeedProg);
        song.progVoiced = getProgVoiced(song.prog, song.progRhythm, song.halfspeedProg);
        song.alt_progVoiced = getProgVoiced(song.prog, song.progRhythm, song.halfspeedProg);

        song.bassSimple = getBassSimple(song.prog, song.progRhythm, song.halfspeedProg);
        song.bassRhythmic = getBassRhythmic(song.prog, song.progRhythm, song.halfspeedProg);

        song.melody = getMelody(song.key, song.prog, song.progRhythm, song.halfspeedProg, song.mode);
        song.harmony = getHarmony(song.key, song.prog, song.progRhythm, song.halfspeedProg, song.melody, song.mode);

        song.alt_melody = getMelody(song.key, song.prog, song.progRhythm, song.halfspeedProg, song.mode);
        song.alt_harmony = getHarmony(song.key, song.prog, song.progRhythm, song.halfspeedProg, song.alt_melody, song.mode);

        song.kickType = Tone.Frequency(between(0, 41) + 23, 'midi');
        song.snareType = Tone.Frequency(between(0, 43) + 23, 'midi');
        song.hatType = Tone.Frequency(between(0, 15) + 23, 'midi');
        song.kickPattern = getKickPattern();
        song.snarePattern = getSnarePattern();
        song.hatPattern = getHatPattern();

        const shortFill = song.bpm < 78 ? true : chance(0.6);
        song.kickFill = getKickFill(song.kickPattern, shortFill);
        song.snareFill = getSnareFill(song.snarePattern, shortFill);
        song.hatFill = getHatFill(song.hatPattern, shortFill);

        const bassPatternProbs = chance(0.7) ? [0.8, 0.2] : [0.2, 0.8];

        // Make sure gain is full at song start
        transport.scheduleOnce((time) => {
            this.gain.gain.setValueAtTime(1, time);
        }, '0:0:0');

        // Bind the play callbacks to `this` / song so sections can call them cleanly.
        const self = this;
        const playBlockChords = (now, eighths, s) => {
            const progPos = eighths % s.progVoiced.length;
            if (s.progVoiced[progPos]) {
                const toPlay = s.progVoiced[progPos];
                self._triggerHumanised(s.chordal, toPlay[0], toPlay[1], now, [0.7, 1], 0.1);
            }
        };
        const playAltBlockChords = (now, eighths, s) => {
            const progPos = eighths % s.alt_progVoiced.length;
            if (s.alt_progVoiced[progPos]) {
                const toPlay = s.alt_progVoiced[progPos];
                self._triggerHumanised(s.chordal, toPlay[0], toPlay[1], now, [0.7, 1], 0.1);
            }
        };
        const playSimpleBass = (now, eighths, s) => {
            const pos = eighths % s.bassSimple.length;
            if (s.bassSimple[pos] && s.bass) {
                const toPlay = s.bassSimple[pos];
                s.bass.triggerAttackRelease(toPlay[0], toPlay[1], now, 1);
            }
        };
        const playRhythmicBass = (now, eighths, s) => {
            const pos = eighths % s.bassRhythmic.length;
            if (s.bassRhythmic[pos] && s.bass) {
                const toPlay = s.bassRhythmic[pos];
                s.bass.triggerAttackRelease(toPlay[0], toPlay[1], now, 1);
            }
        };
        const playSixteenths = (now, eighths, instrument, notes, humanised = false) => {
            const pos = eighths % notes.length;
            if (notes[pos]) {
                const toPlay = notes[pos];
                for (let i = 0; i < 2; i++) {
                    if (toPlay[i]) {
                        const t = now + i * Tone.Time('16n').toSeconds();
                        if (humanised) {
                            self._triggerHumanised(instrument, [toPlay[i][0]], toPlay[i][1], t, [0.8, 1], 0.03);
                        } else {
                            instrument.triggerAttackRelease(toPlay[i][0], toPlay[i][1], t, 1);
                        }
                    }
                }
            }
        };
        const playMelody = (now, eighths, s) => { if (s.lead) playSixteenths(now, eighths, s.lead, s.melody, true); };
        const playAltMelody = (now, eighths, s) => { if (s.lead) playSixteenths(now, eighths, s.lead, s.alt_melody, true); };
        const playHarmony = (now, eighths, s) => { if (s.lead) playSixteenths(now, eighths, s.lead, s.harmony, true); };
        const playAltHarmony = (now, eighths, s) => { if (s.lead) playSixteenths(now, eighths, s.lead, s.alt_harmony, true); };

        const playKick = (now, eighths, s) => {
            const beatPos = eighths % 16;
            if (s.kickPattern[beatPos] && self.kick && self.kick.sampler) {
                self.kick.sampler.triggerAttackRelease(s.kickType, '8n', now, 1);
                self._duck(now, s.bassDucking);
            }
        };
        const playSnare = (now, eighths, s) => {
            const beatPos = eighths % 16;
            if (s.snarePattern[beatPos] && self.snare && self.snare.sampler) {
                self.snare.sampler.triggerAttackRelease(s.snareType, '8n', now, 1);
            }
        };
        const playHat = (now, eighths, s) => {
            const beatPos = eighths % 16;
            if (s.hatPattern[beatPos] && self.hat && self.hat.sampler) {
                self.hat.sampler.triggerAttackRelease(s.hatType, '8n', now + between(0, 0.03), between(0.7, 1));
            }
        };
        const playBeat = (now, eighths, s) => {
            playKick(now, eighths, s);
            playSnare(now, eighths, s);
            playHat(now, eighths, s);
        };
        const playKickFill = (now, pos, s) => {
            const toPlay = s.kickFill[pos];
            if (!toPlay) return;
            for (let i = 0; i < 2; i++) {
                if (toPlay[i] && self.kick && self.kick.sampler) {
                    const t = now + i * Tone.Time('16n').toSeconds();
                    self.kick.sampler.triggerAttackRelease(s.kickType, '4n', t, 1);
                    self._duck(t, s.bassDucking);
                }
            }
        };
        const playSnareFill = (now, pos, s) => {
            const toPlay = s.snareFill[pos];
            if (!toPlay) return;
            for (let i = 0; i < 2; i++) {
                if (toPlay[i] && self.snare && self.snare.sampler) {
                    const t = now + i * Tone.Time('16n').toSeconds();
                    self.snare.sampler.triggerAttackRelease(s.snareType, '8n', t, 1);
                }
            }
        };
        const playHatFill = (now, pos, s) => {
            const toPlay = s.hatFill[pos];
            if (!toPlay) return;
            for (let i = 0; i < 2; i++) {
                if (toPlay[i] && self.hat && self.hat.sampler) {
                    const t = now + i * Tone.Time('16n').toSeconds();
                    self.hat.sampler.triggerAttackRelease(s.hatType, '8n', t + between(0, 0.03), between(0.7, 1));
                }
            }
        };
        const playBeatFill = (now, eighths, s) => {
            const pos = eighths % 32;
            playKickFill(now, pos, s);
            playSnareFill(now, pos, s);
            playHatFill(now, pos, s);
        };

        // Thingdoms addition: play normal beat, but switch to a fill in the
        // LAST BAR of each 4-bar section (eighths 24-31 within a 32-eighth
        // phrase). This makes transitions feel musical rather than abrupt.
        const playBeatWithEndFill = (now, eighths, s) => {
            const posInPhrase = eighths % 32;
            if (posInPhrase >= 24) {
                playBeatFill(now, eighths, s);
            } else {
                playBeat(now, eighths, s);
            }
        };

        // Chord stab on the last 16th of each 4-bar phrase — a short chord
        // punch right before the next section kicks in. Uses the current
        // chord voicing at soft velocity so it punctuates without dominating.
        const playChordStab = (now, eighths, s) => {
            const posInPhrase = eighths % 32;
            // Fire once, on the very last eighth of the phrase
            if (posInPhrase !== 31) return;
            const stabIdx = Math.min(s.progVoiced.length - 1, Math.floor(between(0, s.progVoiced.length)));
            const voicing = s.progVoiced[stabIdx];
            if (!voicing || !voicing[0]) return;
            const notes = voicing[0];
            if (s.chordal) {
                s.chordal.triggerAttackRelease(notes, '16n', now, between(0.45, 0.65));
            }
        };

        // Define sections
        const introA = getSection(song, chooseSome(
            [playBlockChords, 1],
            [playMelody, 0.5],
            choose([
                [playHat, 0.5],
                [playSnare, 0.2],
                [playKick, 0.3],
            ]),
            [playSimpleBass, 0.15],
        ));
        const introB = getSection(song, chooseSome(
            choose([[playBlockChords, 1], [playAltBlockChords, 1]]),
            [playMelody, 0.75],
            [playBeatWithEndFill, 1],
            choose([[playSimpleBass, 1], [playRhythmicBass, 1]], bassPatternProbs),
        ));
        const sectionA = getSection(song, chooseSome(
            choose([[playBlockChords, 1], [playAltBlockChords, 1]]),
            [playMelody, 1],
            [playHarmony, 0.75],
            [playBeatWithEndFill, 1],
            [playChordStab, 0.4],
            choose([[playSimpleBass, 1], [playRhythmicBass, 1]], bassPatternProbs),
        ));
        const sectionB = getSection(song, chooseSome(
            choose([[playBlockChords, 1], [playAltBlockChords, 1]]),
            [playMelody, 1],
            [playHarmony, 1],
            [playBeatWithEndFill, 0.8],
            [playChordStab, 0.5],
            choose([[playSimpleBass, 1], [playRhythmicBass, 1]], bassPatternProbs),
        ));
        const sectionC = getSection(song, chooseSome(
            choose([[playBlockChords, 1], [playAltBlockChords, 1]]),
            [playAltMelody, 1],
            [playAltHarmony, 0.5],
            [playBeatWithEndFill, 1],
            [playChordStab, 0.4],
            choose([[playSimpleBass, 1], [playRhythmicBass, 1]], bassPatternProbs),
        ));
        const sectionD = getSection(song, chooseSome(
            choose([[playBlockChords, 1], [playAltBlockChords, 1]]),
            choose([[playMelody, 0.7], [playAltMelody, 0.7]]),
            [playHarmony, 0.5],
            choose([
                [playBeatWithEndFill, 0.8],
                [playKick, 1],
                [playSnare, 1],
            ], [0.68, 0.16, 0.16]),
            [playChordStab, 0.35],
            choose([[playSimpleBass, 1], [playRhythmicBass, 1]], bassPatternProbs),
        ));

        transport.scheduleRepeat(introA, '8n', '0:0:0', '4:0:0');
        transport.scheduleRepeat(introB, '8n', '4:0:0', choose(['3:3:0', '3:2:0', '4:0:0']));
        transport.scheduleRepeat(sectionA, '8n', '8:0:0', '4:0:0');
        transport.scheduleRepeat(sectionB, '8n', '12:0:0', choose(['3:3:0', '3:2:0', '4:0:0']));
        transport.scheduleRepeat(sectionC, '8n', '16:0:0', '4:0:0');
        transport.scheduleRepeat(sectionD, '8n', '20:0:0', choose(['3:3:0', '3:2:0', '4:0:0']));
        transport.scheduleRepeat(choose([sectionA, sectionB]), '8n', '24:0:0', '4:0:0');
        transport.scheduleRepeat(choose([introA, introB]), '8n', '28:0:0', '6:0:0');

        // Ambience/vinyl + filter automation
        transport.scheduleOnce((time) => {
            if (song.ambience && song.ambience.buffer) {
                song.ambience.start(time, between(0, song.ambience.buffer.duration));
            }
        }, '0:0:0');
        transport.scheduleOnce((time) => {
            if (song.vinyl && song.vinyl.buffer) {
                song.vinyl.start(time, between(0, song.vinyl.buffer.duration));
            }
        }, '0:0:0');
        transport.scheduleOnce((time) => {
            self._setFilter(choose([1, 2]), time);
        }, '0:0:0');
        transport.scheduleOnce((time) => {
            self._rampRemoveFilter(time, choose(['4:0:0', '8:0:0']));
        }, '0:0:0');

        if (chance(0.5)) {
            const bar = 12 + 4 * choose([0, 1, 2, 3]);
            const addAt = (bar - 1).toString() + ':3:3';
            const removeAt = (bar + choose([1, 3])).toString() + ':3:3';
            transport.scheduleOnce((time) => self._rampAddFilter(time, '0:0:1'), addAt);
            transport.scheduleOnce((time) => self._rampRemoveFilter(time, '0:0:1'), removeAt);
        }

        transport.scheduleOnce((time) => { self._fadeOut(time); }, '28:0:0');
        // Auto-advance to the next track after fade
        transport.scheduleOnce(() => {
            if (self._autoSkip) self.skip();
        }, '34:0:0');

        return song;
    }

    _triggerHumanised(instrument, notes, duration, time, velRange, maxDelay) {
        if (!instrument) return;
        for (let i = 0; i < notes.length; i++) {
            instrument.triggerAttackRelease(
                notes[i], duration, time + between(0, maxDelay),
                between(velRange[0], velRange[1]),
            );
        }
    }
}

export default LofigenEngine;
